import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth, API } from '../App';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import axios from 'axios';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import RichTextEditor from '../components/RichTextEditor';
import { TemplateSelector, DocumentPreviewModal, DOCUMENT_TEMPLATES } from '../components/DocumentPreview';
import { exportToWord } from '../utils/wordExport';
import { exportFinancialExcel } from '../utils/financialExcelExport';
import PaymentModal from '../components/PaymentModal';
import { 
  Globe, LogOut, Video, FileText, CreditCard, BookOpen, 
  HelpCircle, Play, Lock, Unlock, Check, ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
  Plus, Send, User, Bot, Loader2, Briefcase, MessageCircle,
  Receipt, Settings, Key, UserCircle, X, Edit3, AlertCircle,
  Sparkles, Zap, Brain, Rocket, Target, CheckCircle2, Clock,
  Search, TrendingUp, Lightbulb, Shield, Mic, Save,
  FileEdit, MessageSquare, RefreshCw, Download, Copy, Bold,
  Italic, Underline, List, ListOrdered, AlignLeft, AlignCenter,
  Eye, EyeOff, Palette, LayoutTemplate, FileDown, Database, Cpu, Cog,
  Network, FileSearch, Wand2, PartyPopper, Layers, BookMarked, Table2, ImagePlus,
  Calendar, ExternalLink, Phone, Mail, Ticket, Building2, Link2, ShoppingCart, Bell, Menu
} from 'lucide-react';

// Section data
const TECHNICAL_SECTIONS = [
  { id: 'executive_summary', title: 'Executive Summary', description: 'High-level overview', category: 'overview' },
  { id: 'problem', title: 'The Problem', description: 'Problem you are solving', category: 'overview' },
  { id: 'solution', title: 'Our Solution', description: 'Your unique solution', category: 'overview' },
  { id: 'vision_mission', title: 'Vision & Mission', description: 'Vision and mission', category: 'overview' },
  { id: 'usps', title: 'USPs', description: 'What makes you different', category: 'overview' },
  { id: 'objectives', title: 'Objectives', description: 'Business objectives', category: 'strategy' },
  { id: 'technology', title: 'Technology', description: 'Tech architecture', category: 'strategy' },
  { id: 'five_year_plan', title: 'Five-Year Plan', description: 'Long-term roadmap', category: 'strategy' },
  { id: 'innovation', title: 'Innovation', description: 'How you innovate', category: 'strategy' },
  { id: 'viability', title: 'Viable Business', description: 'Business viability', category: 'validation' },
  { id: 'scalability', title: 'Scalable Business', description: 'How you scale', category: 'validation' },
  { id: 'roadmap', title: 'Roadmap', description: 'Implementation plan', category: 'validation' },
  { id: 'uk_investment', title: 'UK Investment', description: 'UK investment plans', category: 'location' },
  { id: 'founder', title: 'Founder Profile', description: 'About the founder', category: 'team' },
  { id: 'staffing', title: 'Staffing Plan', description: 'Team structure', category: 'team' },
  { id: 'marketplace', title: 'Marketplace', description: 'Market opportunity', category: 'market' },
  { id: 'competitors', title: 'Competitors', description: 'Competitor analysis', category: 'market' },
  { id: 'swot', title: 'SWOT Analysis', description: 'Strengths & weaknesses', category: 'market' },
  { id: 'marketing_plan', title: 'Marketing Plan', description: 'Go-to-market strategy', category: 'marketing' },
  { id: 'investment_plans', title: 'Investment Plans', description: 'Use of investment', category: 'financial' },
  { id: 'pricing', title: 'Pricing Strategy', description: 'Pricing model', category: 'financial' },
  { id: 'revenue_forecast', title: 'Revenue Forecasts', description: '5-year projections', category: 'financial' },
  { id: 'cashflow', title: 'Cash Flow Forecast', description: 'Cash flow projections', category: 'financial' },
  { id: 'pnl', title: 'P&L Forecast', description: 'Profit & loss', category: 'financial' },
  { id: 'risk_assessment', title: 'Risk Assessment', description: 'Risk mitigation', category: 'risk' },
];

const VIDEO_CHAPTERS_DEFAULT = [
  { id: '1', title: 'Introduction to Innovator Visa', duration_minutes: 15, video_url: 'https://iframe.mediadelivery.net/play/683427/83362523-d284-4011-89ec-483783f88af4' },
  { id: '2', title: 'Understanding Endorsing Bodies', duration_minutes: 20, video_url: null },
  { id: '3', title: 'Validating Your Business Idea', duration_minutes: 25, video_url: null },
  { id: '4', title: 'Crafting Your Technical Plan', duration_minutes: 30, video_url: null },
  { id: '5', title: 'Financial Planning & Projections', duration_minutes: 25, video_url: null },
  { id: '6', title: 'Common Mistakes to Avoid', duration_minutes: 20, video_url: null },
  { id: '7', title: 'Submission & Next Steps', duration_minutes: 15, video_url: null },
];

// Video Comments Component
function VideoComments({ chapterId, chapterTitle, token }) {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [forwardingId, setForwardingId] = useState(null);

  useEffect(() => {
    if (chapterId && token) {
      fetchComments();
    }
  }, [chapterId, token]);

  const fetchComments = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/video-comments?chapter_id=${chapterId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments(res.data.comments || []);
    } catch (err) {
      console.error('Failed to fetch comments:', err);
    } finally {
      setLoading(false);
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    
    try {
      setSubmitting(true);
      const res = await axios.post(`${API}/video-comments`, {
        chapter_id: chapterId,
        chapter_title: chapterTitle,
        content: newComment.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.success) {
        setComments([res.data.comment, ...comments]);
        setNewComment('');
        toast.success('Comment posted! AI has responded.');
      }
    } catch (err) {
      toast.error('Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  const forwardToAhmad = async (commentId) => {
    setForwardingId(commentId);
    try {
      const res = await axios.post(`${API}/video-comments/${commentId}/forward-to-ahmad`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        if (res.data.already_forwarded) {
          toast.info('This question was already forwarded to Ahmad');
        } else {
          toast.success(`Question forwarded to Ahmad! Ticket: ${res.data.ticket_number}`);
        }
        // Update comment in local state
        setComments(prev => prev.map(c => 
          c.id === commentId ? { ...c, forwarded_to_support: true } : c
        ));
      }
    } catch (err) {
      toast.error('Failed to forward question');
    } finally {
      setForwardingId(null);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="mt-6 bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="p-4 border-b border-slate-200 bg-slate-50">
        <h3 className="font-bold text-slate-900 flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-red-500" />
          Comments & Questions
        </h3>
        <p className="text-sm text-slate-500">Ask questions about this chapter - AI will respond instantly!</p>
      </div>
      
      {/* Comment Form */}
      <form onSubmit={submitComment} className="p-4 border-b border-slate-200" data-testid="video-comment-form">
        <div className="flex gap-3">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <User className="w-5 h-5 text-red-600" />
            </div>
          </div>
          <div className="flex-1">
            <textarea
              data-testid="video-comment-textarea"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Write a comment or ask a question about this chapter..."
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none text-sm"
              rows={3}
              maxLength={2000}
            />
            <div className="flex justify-between items-center mt-2">
              <span className="text-xs text-slate-400">{newComment.length}/2000</span>
              <Button 
                data-testid="post-comment-btn"
                type="submit" 
                disabled={submitting || !newComment.trim()}
                className="bg-red-500 hover:bg-red-600"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Posting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Post Comment
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </form>
      
      {/* Comments List */}
      <div className="max-h-96 overflow-y-auto">
        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
          </div>
        ) : comments.length === 0 ? (
          <div className="p-8 text-center">
            <MessageCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No comments yet. Be the first to ask a question!</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {comments.map((comment) => (
              <div key={comment.id} className="p-4">
                {/* User Comment */}
                <div className="flex gap-3 mb-3">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                      <span className="text-sm font-medium text-slate-600">
                        {(comment.user_name || 'U')[0].toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-slate-900 text-sm">{comment.user_name}</span>
                      <span className="text-xs text-slate-400">{formatDate(comment.created_at)}</span>
                    </div>
                    <p className="text-slate-700 text-sm">{comment.content}</p>
                  </div>
                </div>
                
                {/* AI Response */}
                {comment.ai_response && (
                  <div data-testid={`comment-ai-response-${comment.id}`} className="ml-11 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
                        <Bot className="w-4 h-4 text-white" />
                      </div>
                      <span className="font-medium text-blue-900 text-sm">AI Assistant</span>
                    </div>
                    <p className="text-blue-800 text-sm whitespace-pre-wrap">{comment.ai_response}</p>
                    
                    {/* Forward to Ahmad Button */}
                    {!comment.admin_reply && !comment.forwarded_to_support && (
                      <div className="mt-3 pt-3 border-t border-blue-200">
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-blue-600">Need personalized guidance?</p>
                          <button
                            onClick={() => forwardToAhmad(comment.id)}
                            disabled={forwardingId === comment.id}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-100 rounded-lg hover:bg-purple-200 transition-colors disabled:opacity-50"
                            data-testid={`forward-btn-${comment.id}`}
                          >
                            {forwardingId === comment.id ? (
                              <>
                                <Loader2 className="w-3 h-3 animate-spin" />
                                Forwarding...
                              </>
                            ) : (
                              <>
                                <Send className="w-3 h-3" />
                                Forward to Ahmad
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {/* Forwarded Status */}
                    {comment.forwarded_to_support && !comment.admin_reply && (
                      <div className="mt-3 pt-3 border-t border-blue-200">
                        <div className="flex items-center gap-2 text-purple-600 text-xs">
                          <Clock className="w-3 h-3" />
                          <span>Forwarded to Ahmad - awaiting personal response</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Admin Reply */}
                {comment.admin_reply && (
                  <div data-testid={`comment-admin-reply-${comment.id}`} className="ml-11 mt-2 p-3 bg-green-50 rounded-lg border border-green-100">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                        <Shield className="w-4 h-4 text-white" />
                      </div>
                      <span className="font-medium text-green-900 text-sm">Ahmad Raza</span>
                      <span className="text-xs text-green-600">{formatDate(comment.admin_replied_at)}</span>
                    </div>
                    <p className="text-green-800 text-sm whitespace-pre-wrap">{comment.admin_reply}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function UserDashboard() {
  const navigate = useNavigate();
  const auth = useAuth();
  const user = auth.user;
  const token = auth.token;
  const logout = auth.logout;
  
  const [activeMenu, setActiveMenu] = useState('billing');
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chatMessages, setChatMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [sending, setSending] = useState(false);
  const [showProposalDropdown, setShowProposalDropdown] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newProposalName, setNewProposalName] = useState('');
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [mobileChaptersOpen, setMobileChaptersOpen] = useState(false);
  const userMenuRef = useRef(null);
  const ideaEngineScrollRef = useRef(null);
  const workspaceChatRef = useRef(null);
  const videoIframeRef = useRef(null);
  const videoPlayerRef = useRef(null);
  
  // Dynamic video chapters state
  const [videoChapters, setVideoChapters] = useState(VIDEO_CHAPTERS_DEFAULT);
  const VIDEO_CHAPTERS = videoChapters;
  
  // Section workspace state - Auto-generation flow
  const [activeTab, setActiveTab] = useState('workspace'); // 'workspace' or 'document'
  const [sectionContent, setSectionContent] = useState('');
  const [sectionCitations, setSectionCitations] = useState([]);
  const [sectionSaving, setSectionSaving] = useState(false);
  const [sectionSaved, setSectionSaved] = useState(true);
  const [sectionApproved, setSectionApproved] = useState(false);
  const [generatingContent, setGeneratingContent] = useState(false);
  const [generationStage, setGenerationStage] = useState(''); // Current stage of generation
  const [generationProgress, setGenerationProgress] = useState(0);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [completedSections, setCompletedSections] = useState([]);
  const [typewriterText, setTypewriterText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  
  // Document Preview & Template state
  const [selectedTemplate, setSelectedTemplate] = useState('executive-red');
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [showDocumentPreview, setShowDocumentPreview] = useState(false);
  const [isEditingContent, setIsEditingContent] = useState(false);
  const [exportingWord, setExportingWord] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const workspaceRef = useRef(null);
  
  // AI Idea Engine state
  const [showIdeaEngine, setShowIdeaEngine] = useState(false);
  const [ideaEngineSession, setIdeaEngineSession] = useState(null);
  const [ideaEngineMessages, setIdeaEngineMessages] = useState([]);
  const [ideaEngineInput, setIdeaEngineInput] = useState('');
  const [ideaEngineSending, setIdeaEngineSending] = useState(false);
  const [ideaEngineReady, setIdeaEngineReady] = useState(false);
  const [ideaEngineProposedName, setIdeaEngineProposedName] = useState('');
  const [ideaEngineCategory, setIdeaEngineCategory] = useState('');
  const [ideaEngineQuestionCount, setIdeaEngineQuestionCount] = useState(0);
  const [ideaEngineStarting, setIdeaEngineStarting] = useState(false);
  const [ideaEngineMode, setIdeaEngineMode] = useState(null); // 'have_idea' or 'generate'
  const [ideaEngineStage, setIdeaEngineStage] = useState('welcome');
  const [animatedState, setAnimatedState] = useState(null);
  const [generatedIdeas, setGeneratedIdeas] = useState([]);
  const [creditsRemaining, setCreditsRemaining] = useState(0);
  
  // Locked idea state
  const [lockedIdea, setLockedIdea] = useState(null);
  const [showLockedIdeaModal, setShowLockedIdeaModal] = useState(false);
  
  // Section Image state
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [sectionImages, setSectionImages] = useState({});
  const [searchingImages, setSearchingImages] = useState(false);
  const [imageSearchResults, setImageSearchResults] = useState([]);
  const [imageSearchQuery, setImageSearchQuery] = useState('');
  
  // Mock Interview state
  const [showMockInterview, setShowMockInterview] = useState(false);
  const [interviewCredits, setInterviewCredits] = useState(0);
  const [interviewFreeAvailable, setInterviewFreeAvailable] = useState(false);
  const [showInterviewPurchaseModal, setShowInterviewPurchaseModal] = useState(false);
  const [purchasingInterviewCredit, setPurchasingInterviewCredit] = useState(false);
  
  // AI Section Chat state (side panel)
  const [showAiChatPanel, setShowAiChatPanel] = useState(false);
  const [aiChatMessages, setAiChatMessages] = useState([]);
  const [aiChatInput, setAiChatInput] = useState('');
  const [aiChatSending, setAiChatSending] = useState(false);
  const aiChatScrollRef = useRef(null);
  
  // Support Tickets state
  const [supportTickets, setSupportTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newTicketSubject, setNewTicketSubject] = useState('');
  const [newTicketMessage, setNewTicketMessage] = useState('');
  const [newTicketCategory, setNewTicketCategory] = useState('general');
  const [ticketReply, setTicketReply] = useState('');
  const [creatingTicket, setCreatingTicket] = useState(false);
  const [sendingTicketReply, setSendingTicketReply] = useState(false);
  const [showNewTicketForm, setShowNewTicketForm] = useState(false);
  
  // Zoom Booking state
  const [zoomBookings, setZoomBookings] = useState([]);
  const [selectedBookingDate, setSelectedBookingDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingTopic, setBookingTopic] = useState('');
  const [bookingDescription, setBookingDescription] = useState('');
  const [creatingBooking, setCreatingBooking] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Profile Settings Modal state
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [showAccountSettingsModal, setShowAccountSettingsModal] = useState(false);
  
  // Profile form state
  const [profileName, setProfileName] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordChanging, setPasswordChanging] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Payment Modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPaymentPackage, setSelectedPaymentPackage] = useState(null);

  // Notification state
  const [notifications, setNotifications] = useState([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [showNotificationDropdown, setShowNotificationDropdown] = useState(false);
  const notificationRef = useRef(null);

  // Question Bank state
  const [questionBankItems, setQuestionBankItems] = useState([]);
  const [questionBankStats, setQuestionBankStats] = useState({});
  const [questionBankSearch, setQuestionBankSearch] = useState('');
  const [questionBankLoading, setQuestionBankLoading] = useState(false);
  const [selectedQuestionItem, setSelectedQuestionItem] = useState(null);
  const [forwardingComment, setForwardingComment] = useState(null);

  const hasVideoCourse = purchases.some(p => p.purchase && ['video', 'complete', 'bundle'].includes(p.purchase.package_type));
  const hasProposalAccess = purchases.some(p => p.purchase && ['complete', 'bundle'].includes(p.purchase.package_type));
  
  // Check if user has ANY core package (video, complete, or premium)
  const hasAnyCorePackage = purchases.some(p => 
    p.purchase && ['video', 'complete', 'bundle', 'premium'].includes(p.purchase.package_type)
  );
  
  // State for package selection modal
  const [showPackageGate, setShowPackageGate] = useState(false);
  const [availablePackages, setAvailablePackages] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const userPackage = purchases.find(p => p.purchase)?.purchase;
  const currentPackageType = userPackage?.package_type;
  const packageName = currentPackageType === 'complete' ? 'Complete Package' : 
                      currentPackageType === 'video' ? 'Video Course' :
                      currentPackageType === 'bundle' ? 'Premium Bundle' : 
                      currentPackageType === 'premium' ? 'Premium Service' : 'No Package';
  
  // Determine available upgrades based on current package
  const getUpgradeOptions = () => {
    const packagePrices = availablePackages || {};
    const options = [];
    
    if (!currentPackageType || currentPackageType === 'video') {
      // Can upgrade to Complete or Premium
      options.push({
        type: 'complete',
        name: 'Complete Package',
        price: packagePrices.complete?.price || 149,
        features: [
          'Everything in Video Course',
          'AI Business Plan Builder',
          'Endorsement Readiness Engine',
          'Document Templates',
          'Mock Interview Practice'
        ],
        highlight: 'Most Popular',
        color: 'blue'
      });
    }
    
    if (!currentPackageType || currentPackageType === 'video' || currentPackageType === 'complete') {
      // Can upgrade to Premium
      options.push({
        type: 'premium',
        name: 'Premium Service',
        price: packagePrices.premium?.price || 1999,
        features: [
          'Everything in Complete Package',
          'Done-For-You Application',
          '1-on-1 Coaching Sessions',
          'Direct Support from Ahmad',
          'Guaranteed Endorsement Assistance'
        ],
        highlight: 'Best Value',
        color: 'purple'
      });
    }
    
    return options;
  };
  
  const upgradeOptions = getUpgradeOptions();
  const canUpgrade = upgradeOptions.length > 0;
  
  const ideaCredits = user?.idea_credits || 1;

  useEffect(() => {
    if (!token) return;
    
    const fetchData = async () => {
      try {
        const purchasesRes = await axios.get(API + '/user/purchases', { headers: { Authorization: 'Bearer ' + token } });
        setPurchases(purchasesRes.data);
        
        try {
          const proposalsRes = await axios.get(API + '/ai/proposals', { headers: { Authorization: 'Bearer ' + token } });
          setProposals(proposalsRes.data || []);
          if (proposalsRes.data && proposalsRes.data.length > 0) {
            setSelectedProposal(proposalsRes.data[0]);
          }
        } catch (e) {
          setProposals([]);
        }
        
        // Fetch credit balance
        try {
          const creditsRes = await axios.get(API + '/credits/balance', { headers: { Authorization: 'Bearer ' + token } });
          setCreditsRemaining(creditsRes.data.credits_remaining || 0);
        } catch (e) {
          console.log('Credits not available');
        }
        
        // Fetch interview credits
        try {
          const interviewRes = await axios.get(API + '/mock-interview/credits', { headers: { Authorization: 'Bearer ' + token } });
          setInterviewCredits(interviewRes.data.credits_remaining || 0);
          setInterviewFreeAvailable(interviewRes.data.free_available || false);
        } catch (e) {
          console.log('Interview credits not available');
        }
        
        // Check for locked idea
        try {
          const lockedRes = await axios.get(API + '/ai/proposals/locked', { headers: { Authorization: 'Bearer ' + token } });
          if (lockedRes.data.has_locked_idea) {
            setLockedIdea(lockedRes.data.proposal);
          }
        } catch (e) {
          console.log('Locked idea check not available');
        }
        
        // Fetch video chapters from database (unified endpoint for all packages)
        try {
          const chaptersRes = await axios.get(API + '/courses/video/all-chapters', { 
            headers: { Authorization: 'Bearer ' + token } 
          });
          const dbChapters = chaptersRes.data?.chapters || [];
          if (dbChapters.length > 0) {
            // Use chapters from database
            setVideoChapters(dbChapters);
            setSelectedChapter(dbChapters[0]);
            console.log('Loaded', dbChapters.length, 'chapters from database');
          } else {
            console.log('No chapters in database, using defaults');
          }
        } catch (e) {
          console.log('Using default video chapters:', e.message);
        }
      } catch (err) {
        console.error('Failed to fetch:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [token]);

  // Fetch available packages for gate modal
  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const res = await axios.get(API + '/payments/info');
        setAvailablePackages(res.data.packages);
      } catch (err) {
        console.error('Failed to fetch packages:', err);
      }
    };
    fetchPackages();
  }, []);

  // Fetch notifications
  useEffect(() => {
    if (!token) return;
    
    const fetchNotifications = async () => {
      try {
        const res = await axios.get(API + '/notifications', {
          headers: { Authorization: 'Bearer ' + token }
        });
        setNotifications(res.data.notifications || []);
        setUnreadNotificationCount(res.data.unread_count || 0);
      } catch (err) {
        console.log('Notifications not available');
      }
    };
    
    fetchNotifications();
    // Poll every 60 seconds
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [token]);

  // Close notification dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotificationDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Mark notifications as read
  const markNotificationsRead = async () => {
    try {
      await axios.post(API + '/notifications/mark-read', {}, {
        headers: { Authorization: 'Bearer ' + token }
      });
      setUnreadNotificationCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (err) {
      console.log('Failed to mark notifications as read');
    }
  };

  // Delete notification
  const deleteNotification = async (notifId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(API + '/notifications/' + notifId, {
        headers: { Authorization: 'Bearer ' + token }
      });
      setNotifications(prev => prev.filter(n => n.id !== notifId));
      toast.success('Notification deleted');
    } catch (err) {
      console.log('Failed to delete notification');
      toast.error('Failed to delete notification');
    }
  };

  // Show package gate if user has no core package (after purchases loaded)
  useEffect(() => {
    if (!loading && purchases.length === 0) {
      // User has no purchases at all - show package gate
      setShowPackageGate(true);
    } else if (!loading && !hasAnyCorePackage && purchases.length > 0) {
      // User has some purchases but no core package
      setShowPackageGate(true);
    }
  }, [loading, purchases, hasAnyCorePackage]);

  // Sync completedSections when selectedProposal changes
  useEffect(() => {
    if (selectedProposal && selectedProposal.completed_sections) {
      setCompletedSections(selectedProposal.completed_sections);
    } else {
      setCompletedSections([]);
    }
  }, [selectedProposal?.id]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Video player auto-advance: Listen for video ended event and move to next chapter
  useEffect(() => {
    if (!selectedChapter || !selectedChapter.video_url || !videoIframeRef.current) return;
    
    // Check if playerjs is available (loaded from CDN)
    if (typeof window.playerjs === 'undefined') return;
    
    try {
      const player = new window.playerjs.Player(videoIframeRef.current);
      videoPlayerRef.current = player;
      
      player.on('ended', () => {
        // Auto-advance to next chapter
        const currentIndex = VIDEO_CHAPTERS.findIndex(c => c.id === selectedChapter.id);
        if (currentIndex < VIDEO_CHAPTERS.length - 1) {
          setSelectedChapter(VIDEO_CHAPTERS[currentIndex + 1]);
          toast.success('Auto-advancing to next chapter');
        } else {
          toast.success('Congratulations! You completed the course!');
        }
      });
      
      return () => {
        // Cleanup
        videoPlayerRef.current = null;
      };
    } catch (err) {
      console.log('Player.js not available for this video:', err);
    }
  }, [selectedChapter]);

  // Clean AI message content by removing meta-tags and markdown formatting
  function cleanAIMessage(content) {
    if (!content) return '';
    return content
      // Remove progress tags
      .replace(/\[DISCOVERY_PROGRESS:\s*\d+\/\d+\]/gi, '')
      // Remove ready to finalize tag
      .replace(/\[READY_TO_FINALIZE\]/gi, '')
      // Remove markdown bold/italic
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/\*([^*]+)\*/g, '$1')
      // Remove ```blocks```
      .replace(/```[^`]*```/gs, '')
      // Clean up extra newlines
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  function handleLogout() {
    logout();
    navigate('/');
  }

  function handleMenuClick(menuId) {
    if (menuId === 'hire') { navigate('/white-glove-service'); return; }
    if (menuId === 'support') { fetchSupportTickets(); }
    if (menuId === 'zoom') { fetchZoomBookings(); }
    if (menuId === 'questionbank') { fetchQuestionBank(); }
    setActiveMenu(menuId);
    setSelectedChapter(null);
    setSelectedSection(null);
    setSelectedTicket(null);
    setShowNewTicketForm(false);
    setSelectedQuestionItem(null);
  }

  function isSectionUnlocked(sectionId) {
    const idx = TECHNICAL_SECTIONS.findIndex(s => s.id === sectionId);
    if (idx === 0) return true;
    if (!selectedProposal) return false;
    const prev = TECHNICAL_SECTIONS[idx - 1];
    // Check both proposal's completed_sections AND local state (for recently approved)
    const proposalCompleted = (selectedProposal.completed_sections || []).includes(prev?.id);
    const localCompleted = completedSections.includes(prev?.id);
    return proposalCompleted || localCompleted;
  }

  function isSectionComplete(sectionId) {
    if (!selectedProposal) return false;
    // Check both the proposal's completed_sections AND the local state (for recently approved)
    const proposalCompleted = (selectedProposal.completed_sections || []).includes(sectionId);
    const localCompleted = completedSections.includes(sectionId);
    return proposalCompleted || localCompleted;
  }

  // Purchase interview credit function
  async function purchaseInterviewCredit() {
    setPurchasingInterviewCredit(true);
    try {
      const res = await axios.post(API + '/mock-interview/purchase', {}, {
        headers: { Authorization: 'Bearer ' + token }
      });
      
      if (res.data.success) {
        toast.success('Interview credit purchased successfully!');
        setInterviewCredits(res.data.credits_remaining || 1);
        setInterviewFreeAvailable(false); // No longer free after purchase
        setShowInterviewPurchaseModal(false);
        
        // Refresh credits to get accurate count
        try {
          const creditRes = await axios.get(API + '/mock-interview/credits', { 
            headers: { Authorization: 'Bearer ' + token } 
          });
          setInterviewCredits(creditRes.data.credits_remaining || 0);
          setInterviewFreeAvailable(creditRes.data.free_available || false);
        } catch (e) {
          console.log('Failed to refresh credits');
        }
      }
    } catch (err) {
      console.error('Purchase failed:', err);
      toast.error('Failed to purchase credit. Please try again.');
    } finally {
      setPurchasingInterviewCredit(false);
    }
  }

  // Handle starting mock interview with credit check
  function handleStartMockInterview() {
    const lockedProposal = proposals.find(p => p.status === 'idea_locked');
    if (!lockedProposal) {
      toast.error('Please create and lock a business idea first');
      return;
    }
    
    // Check if user has credits (free or purchased)
    if (!interviewFreeAvailable && interviewCredits <= 0) {
      setShowInterviewPurchaseModal(true);
      return;
    }
    
    // User has credits, navigate to interview
    navigate('/interview/' + lockedProposal.id);
  }

  // ==================== SUPPORT TICKETS ====================
  async function fetchSupportTickets() {
    try {
      const res = await axios.get(API + '/support/tickets', {
        headers: { Authorization: 'Bearer ' + token }
      });
      setSupportTickets(res.data.tickets || []);
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
    }
  }

  async function createSupportTicket() {
    if (!newTicketSubject.trim() || !newTicketMessage.trim()) {
      toast.error('Please fill in subject and message');
      return;
    }
    setCreatingTicket(true);
    try {
      await axios.post(API + '/support/tickets', {
        subject: newTicketSubject,
        message: newTicketMessage,
        category: newTicketCategory
      }, { headers: { Authorization: 'Bearer ' + token } });
      toast.success('Ticket created successfully!');
      setNewTicketSubject('');
      setNewTicketMessage('');
      setNewTicketCategory('general');
      setShowNewTicketForm(false);
      fetchSupportTickets();
    } catch (err) {
      toast.error('Failed to create ticket');
    } finally {
      setCreatingTicket(false);
    }
  }

  async function sendTicketReply() {
    if (!ticketReply.trim() || !selectedTicket) return;
    setSendingTicketReply(true);
    try {
      await axios.post(API + '/support/tickets/' + selectedTicket.id + '/reply', {
        message: ticketReply
      }, { headers: { Authorization: 'Bearer ' + token } });
      toast.success('Reply sent!');
      setTicketReply('');
      // Refresh ticket
      const res = await axios.get(API + '/support/tickets/' + selectedTicket.id, {
        headers: { Authorization: 'Bearer ' + token }
      });
      setSelectedTicket(res.data);
      fetchSupportTickets();
    } catch (err) {
      toast.error('Failed to send reply');
    } finally {
      setSendingTicketReply(false);
    }
  }

  // ==================== ZOOM BOOKINGS ====================
  async function fetchZoomBookings() {
    try {
      const res = await axios.get(API + '/bookings/my-bookings', {
        headers: { Authorization: 'Bearer ' + token }
      });
      setZoomBookings(res.data.bookings || []);
    } catch (err) {
      console.error('Failed to fetch bookings:', err);
    }
  }

  // ==================== QUESTION BANK ====================
  async function fetchQuestionBank() {
    setQuestionBankLoading(true);
    try {
      const res = await axios.get(API + '/knowledge/question-bank', {
        headers: { Authorization: 'Bearer ' + token }
      });
      setQuestionBankItems(res.data.questions || []);
      setQuestionBankStats(res.data.stats || {});
    } catch (err) {
      console.error('Failed to fetch question bank:', err);
    } finally {
      setQuestionBankLoading(false);
    }
  }

  async function searchQuestionBank(query) {
    if (!query || query.length < 2) {
      fetchQuestionBank();
      return;
    }
    setQuestionBankLoading(true);
    try {
      const res = await axios.get(API + '/knowledge/question-bank/search?q=' + encodeURIComponent(query), {
        headers: { Authorization: 'Bearer ' + token }
      });
      setQuestionBankItems(res.data.results || []);
    } catch (err) {
      console.error('Failed to search question bank:', err);
    } finally {
      setQuestionBankLoading(false);
    }
  }

  async function forwardCommentToAhmad(commentId) {
    setForwardingComment(commentId);
    try {
      const res = await axios.post(API + '/video-comments/' + commentId + '/forward-to-ahmad', {}, {
        headers: { Authorization: 'Bearer ' + token }
      });
      if (res.data.success) {
        if (res.data.already_forwarded) {
          toast.info('This question was already forwarded to Ahmad');
        } else {
          toast.success('Question forwarded to Ahmad! Ticket: ' + res.data.ticket_number);
        }
        // Refresh question bank
        fetchQuestionBank();
      }
    } catch (err) {
      toast.error('Failed to forward question');
    } finally {
      setForwardingComment(null);
    }
  }

  async function fetchAvailableSlots(date) {
    setLoadingSlots(true);
    try {
      const res = await axios.get(API + '/bookings/available-slots?date=' + date, {
        headers: { Authorization: 'Bearer ' + token }
      });
      setAvailableSlots(res.data.available_slots || []);
    } catch (err) {
      console.error('Failed to fetch slots:', err);
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  }

  async function createZoomBooking() {
    if (!selectedBookingDate || !selectedSlot || !bookingTopic.trim() || !bookingDescription.trim()) {
      toast.error('Please fill in all booking details');
      return;
    }
    setCreatingBooking(true);
    try {
      // Format date to YYYY-MM-DD
      const dateStr = selectedBookingDate instanceof Date 
        ? selectedBookingDate.toISOString().split('T')[0]
        : selectedBookingDate;
      
      const res = await axios.post(API + '/bookings/book', {
        date: dateStr,
        time_slot: selectedSlot,
        topic: bookingTopic,
        description: bookingDescription
      }, { headers: { Authorization: 'Bearer ' + token } });
      
      // Create Stripe checkout session
      const payRes = await axios.post(API + '/bookings/book/' + res.data.booking_id + '/pay', {}, {
        headers: { Authorization: 'Bearer ' + token }
      });
      
      // Redirect to Stripe checkout
      if (payRes.data.checkout_url) {
        window.location.href = payRes.data.checkout_url;
      } else {
        toast.error('Failed to create payment session');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to book call');
      setCreatingBooking(false);
    }
  }

  async function cancelZoomBooking(bookingId) {
    try {
      await axios.delete(API + '/bookings/cancel/' + bookingId, {
        headers: { Authorization: 'Bearer ' + token }
      });
      toast.success('Booking cancelled');
      fetchZoomBookings();
    } catch (err) {
      toast.error('Failed to cancel booking');
    }
  }

  // ==================== PROFILE SETTINGS ====================
  function openProfileModal() {
    setProfileName(user?.name || '');
    setShowProfileModal(true);
    setShowUserMenu(false);
  }

  async function saveProfileSettings() {
    if (!profileName.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    setProfileSaving(true);
    try {
      await axios.patch(API + '/user/profile', {
        name: profileName
      }, { headers: { Authorization: 'Bearer ' + token } });
      toast.success('Profile updated successfully!');
      setShowProfileModal(false);
      // Update local user state if auth context provides a way
      if (auth.refreshUser) {
        auth.refreshUser();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setProfileSaving(false);
    }
  }

  function openPasswordModal() {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowPasswordModal(true);
    setShowUserMenu(false);
  }

  async function changePassword() {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setPasswordChanging(true);
    try {
      await axios.post(API + '/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      }, { headers: { Authorization: 'Bearer ' + token } });
      toast.success('Password changed successfully!');
      setShowPasswordModal(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasswordChanging(false);
    }
  }

  function openBillingModal() {
    setShowBillingModal(true);
    setShowUserMenu(false);
  }

  function openAccountSettingsModal() {
    setShowAccountSettingsModal(true);
    setShowUserMenu(false);
  }

  // Convert plain text to HTML for TipTap editor
  function textToHtml(text) {
    if (!text) return '';
    // Check if already HTML (has tags including tables)
    if (text.includes('<p>') || text.includes('<br>') || text.includes('<h') || text.includes('<table')) {
      return text;
    }
    
    // If text has line breaks, convert them to paragraphs
    if (text.includes('\n')) {
      const lines = text.split('\n');
      const htmlParts = [];
      let currentParagraph = '';
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (line === '') {
          // Empty line = paragraph break
          if (currentParagraph) {
            htmlParts.push('<p>' + currentParagraph + '</p>');
            currentParagraph = '';
          }
        } else {
          if (currentParagraph) {
            currentParagraph += '<br>' + line;
          } else {
            currentParagraph = line;
          }
        }
      }
      // Add remaining paragraph
      if (currentParagraph) {
        htmlParts.push('<p>' + currentParagraph + '</p>');
      }
      return htmlParts.join('') || '<p>' + text + '</p>';
    }
    
    // No line breaks - wrap in paragraph
    return '<p>' + text + '</p>';
  }

  // Convert HTML back to plain text for storage
  function htmlToText(html) {
    if (!html) return '';
    // Replace paragraph endings with double newlines
    let text = html.replace(/<\/p>\s*<p>/gi, '\n\n');
    // Replace br tags with single newlines
    text = text.replace(/<br\s*\/?>/gi, '\n');
    // Remove remaining HTML tags
    text = text.replace(/<[^>]+>/g, '');
    // Decode HTML entities
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    text = textarea.value;
    return text.trim();
  }

  async function handleCreateProposal() {
    // Check if user already has a locked idea
    if (lockedIdea) {
      setShowLockedIdeaModal(true);
      return;
    }
    
    // Check credits before starting
    if (creditsRemaining <= 0) {
      toast.error('You need idea discussion credits to create a new proposal. Please purchase more credits.');
      return;
    }
    // Reset state and open the AI Idea Engine modal
    setShowIdeaEngine(true);
    setIdeaEngineStarting(true);
    setIdeaEngineMode(null);
    setIdeaEngineStage('welcome');
    setGeneratedIdeas([]);
    setAnimatedState(null);
    
    try {
      const res = await axios.post(API + '/ai/idea-engine-v2/start', {}, { headers: { Authorization: 'Bearer ' + token } });
      setIdeaEngineSession(res.data.session_id);
      setIdeaEngineMessages([{ role: 'assistant', content: res.data.message }]);
      setIdeaEngineStage(res.data.stage || 'welcome');
      setCreditsRemaining(res.data.credits_remaining || creditsRemaining);
      setIdeaEngineReady(false);
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error('Insufficient credits. Please purchase more credits to continue.');
      } else if (err.response?.status === 403 && err.response?.data?.error === 'idea_already_locked') {
        // Handle locked idea from backend
        setLockedIdea(err.response.data.locked_idea);
        setShowLockedIdeaModal(true);
      } else {
        toast.error('Failed to start AI Idea Engine');
      }
      setShowIdeaEngine(false);
    } finally {
      setIdeaEngineStarting(false);
    }
  }

  async function handleIdeaEngineChat() {
    if (!ideaEngineInput.trim() || ideaEngineSending || !ideaEngineSession) return;
    
    const userMsg = ideaEngineInput.trim();
    setIdeaEngineMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setIdeaEngineInput('');
    setIdeaEngineSending(true);
    setAnimatedState('THINKING');

    try {
      const res = await axios.post(API + '/ai/idea-engine-v2/chat', { 
        session_id: ideaEngineSession, 
        message: userMsg 
      }, { headers: { Authorization: 'Bearer ' + token } });
      
      // Handle animated state from response
      if (res.data.animated_state) {
        setAnimatedState(res.data.animated_state);
        await new Promise(resolve => setTimeout(resolve, 1500));
      }
      setAnimatedState(null);
      
      setIdeaEngineMessages(prev => [...prev, { role: 'assistant', content: res.data.message }]);
      setIdeaEngineStage(res.data.stage || ideaEngineStage);
      setIdeaEngineMode(res.data.mode || ideaEngineMode);
      setCreditsRemaining(res.data.credits_remaining);
      
      // Handle generated ideas
      if (res.data.ideas_ready && res.data.generated_ideas) {
        setGeneratedIdeas(res.data.generated_ideas);
      }
      
      if (res.data.ready_to_lock) {
        setIdeaEngineReady(true);
        // Try to extract name from the last AI message or selected idea
        if (res.data.generated_ideas && res.data.generated_ideas.length > 0) {
          setIdeaEngineProposedName(res.data.generated_ideas[0].name || '');
          setIdeaEngineCategory(res.data.generated_ideas[0].category || 'Tech');
        }
      }
      
      // Auto-scroll to bottom
      setTimeout(() => {
        if (ideaEngineScrollRef.current) {
          ideaEngineScrollRef.current.scrollTop = ideaEngineScrollRef.current.scrollHeight;
        }
      }, 100);
    } catch (err) {
      setAnimatedState(null);
      if (err.response?.status === 402) {
        toast.error('Insufficient credits. Please purchase more credits to continue.');
      } else {
        toast.error('Failed to send message');
      }
    } finally {
      setIdeaEngineSending(false);
    }
  }

  async function handleFinalizeIdea() {
    if (!ideaEngineProposedName.trim()) {
      toast.error('Please enter a name for your business idea');
      return;
    }
    
    try {
      const res = await axios.post(API + '/ai/idea-engine-v2/finalize', {
        session_id: ideaEngineSession,
        idea_name: ideaEngineProposedName.trim(),
        idea_category: ideaEngineCategory || 'Tech'
      }, { headers: { Authorization: 'Bearer ' + token } });
      
      // Refresh proposals list from the proposals collection
      try {
        const proposalsRes = await axios.get(API + '/ai/proposals', { headers: { Authorization: 'Bearer ' + token } });
        setProposals(proposalsRes.data || []);
        
        // Find and select the new proposal
        const newProposal = proposalsRes.data.find(p => p.id === res.data.proposal_id);
        if (newProposal) {
          setSelectedProposal(newProposal);
          setSelectedSection('executive_summary');
        }
      } catch (e) {
        // If proposals endpoint doesn't return the new one, create a local version
        const newProposal = {
          id: res.data.proposal_id,
          idea_title: res.data.idea_title,
          category: res.data.category,
          status: 'idea_locked',
          completed_sections: []
        };
        setProposals(prev => [newProposal, ...prev]);
        setSelectedProposal(newProposal);
        setSelectedSection('executive_summary');
      }
      
      // Reset and close
      setShowIdeaEngine(false);
      setIdeaEngineSession(null);
      setIdeaEngineMessages([]);
      setIdeaEngineInput('');
      setIdeaEngineReady(false);
      setIdeaEngineProposedName('');
      setIdeaEngineCategory('');
      setGeneratedIdeas([]);
      setIdeaEngineMode(null);
      setIdeaEngineStage('welcome');
      
      toast.success('Your business idea has been locked! Start building your proposal.');
    } catch (err) {
      toast.error('Failed to finalize idea');
    }
  }

  function handleCancelIdeaEngine() {
    if (ideaEngineSession) {
      axios.delete(API + '/ai/idea-engine-v2/session/' + ideaEngineSession, { headers: { Authorization: 'Bearer ' + token } }).catch(() => {});
    }
    setShowIdeaEngine(false);
    setIdeaEngineSession(null);
    setIdeaEngineMessages([]);
    setIdeaEngineInput('');
    setIdeaEngineReady(false);
    setIdeaEngineProposedName('');
    setIdeaEngineCategory('');
    setGeneratedIdeas([]);
    setIdeaEngineMode(null);
    setIdeaEngineStage('welcome');
    setAnimatedState(null);
  }

  async function handleRenameProposal() {
    if (!renameValue.trim() || !selectedProposal) return;
    
    try {
      await axios.patch(API + '/ai/proposals/' + selectedProposal.id, { name: renameValue.trim() }, { headers: { Authorization: 'Bearer ' + token } });
      setProposals(prev => prev.map(p => p.id === selectedProposal.id ? { ...p, idea_title: renameValue.trim() } : p));
      setSelectedProposal(prev => ({ ...prev, idea_title: renameValue.trim() }));
      setShowRenameModal(false);
      setRenameValue('');
      toast.success('Proposal renamed!');
    } catch (err) {
      toast.error('Failed to rename proposal');
    }
  }

  // ==================== SECTION AUTO-GENERATION FLOW ====================

  // Generation animation stages
  // Section-specific generation messages for a tailored experience
  const SECTION_STAGES = {
    executive_summary: [
      { id: 'connecting', text: 'Connecting to Neural Proposal Engine...', icon: Database, duration: 1500 },
      { id: 'loading', text: 'Loading your validated business concept...', icon: Layers, duration: 1800 },
      { id: 'analyzing', text: 'Analyzing executive summary frameworks...', icon: FileSearch, duration: 2000 },
      { id: 'extracting', text: 'Extracting key value propositions...', icon: Target, duration: 2200 },
      { id: 'reasoning', text: 'Activating Strategic Reasoning Module...', icon: Cpu, duration: 2500 },
      { id: 'crafting', text: 'Composing compelling executive narrative...', icon: Wand2, duration: 2800 },
      { id: 'polishing', text: 'Refining for endorsing body standards...', icon: Sparkles, duration: 1500 },
    ],
    problem: [
      { id: 'connecting', text: 'Connecting to Market Intelligence Engine...', icon: Database, duration: 1500 },
      { id: 'analyzing', text: 'Analyzing pain points & market gaps...', icon: Search, duration: 2000 },
      { id: 'researching', text: 'Researching industry challenges...', icon: TrendingUp, duration: 2200 },
      { id: 'validating', text: 'Validating problem significance...', icon: CheckCircle2, duration: 2000 },
      { id: 'crafting', text: 'Articulating the problem statement...', icon: Wand2, duration: 2500 },
      { id: 'polishing', text: 'Ensuring endorsing body alignment...', icon: Sparkles, duration: 1500 },
    ],
    solution: [
      { id: 'connecting', text: 'Connecting to Innovation Framework...', icon: Database, duration: 1500 },
      { id: 'analyzing', text: 'Analyzing your unique solution approach...', icon: Lightbulb, duration: 2000 },
      { id: 'comparing', text: 'Benchmarking against alternatives...', icon: Search, duration: 2200 },
      { id: 'highlighting', text: 'Highlighting innovation factors...', icon: Zap, duration: 2000 },
      { id: 'crafting', text: 'Crafting compelling solution narrative...', icon: Wand2, duration: 2500 },
      { id: 'polishing', text: 'Optimizing for visa requirements...', icon: Sparkles, duration: 1500 },
    ],
    investment_plans: [
      { id: 'connecting', text: 'Connecting to Financial Planning Module...', icon: Database, duration: 1500 },
      { id: 'analyzing', text: 'Analyzing capital requirements...', icon: CreditCard, duration: 2000 },
      { id: 'modeling', text: 'Modeling investment allocation...', icon: Cpu, duration: 2500 },
      { id: 'benchmarking', text: 'Benchmarking UK startup funding norms...', icon: TrendingUp, duration: 2200 },
      { id: 'structuring', text: 'Structuring use of funds breakdown...', icon: Layers, duration: 2000 },
      { id: 'crafting', text: 'Drafting investment narrative...', icon: Wand2, duration: 2500 },
      { id: 'polishing', text: 'Aligning with endorsing body expectations...', icon: Sparkles, duration: 1500 },
    ],
    pricing: [
      { id: 'connecting', text: 'Connecting to Pricing Intelligence Engine...', icon: Database, duration: 1500 },
      { id: 'researching', text: 'Researching market pricing models...', icon: Search, duration: 2000 },
      { id: 'analyzing', text: 'Analyzing competitor pricing strategies...', icon: TrendingUp, duration: 2200 },
      { id: 'modeling', text: 'Modeling optimal price points...', icon: Cpu, duration: 2500 },
      { id: 'crafting', text: 'Crafting pricing strategy rationale...', icon: Wand2, duration: 2500 },
      { id: 'polishing', text: 'Finalizing pricing section...', icon: Sparkles, duration: 1500 },
    ],
    revenue_forecast: [
      { id: 'connecting', text: 'Connecting to Financial Projection Engine...', icon: Database, duration: 1500 },
      { id: 'modeling', text: 'Building 5-year revenue models...', icon: Cpu, duration: 2500 },
      { id: 'analyzing', text: 'Analyzing growth assumptions...', icon: TrendingUp, duration: 2200 },
      { id: 'scenarios', text: 'Running scenario simulations...', icon: Network, duration: 2500 },
      { id: 'validating', text: 'Validating projections against benchmarks...', icon: CheckCircle2, duration: 2000 },
      { id: 'crafting', text: 'Formatting professional forecast tables...', icon: Wand2, duration: 2500 },
      { id: 'polishing', text: 'Ensuring investor-ready presentation...', icon: Sparkles, duration: 1500 },
    ],
    risk_assessment: [
      { id: 'connecting', text: 'Connecting to Risk Analysis Framework...', icon: Database, duration: 1500 },
      { id: 'identifying', text: 'Identifying potential risk factors...', icon: AlertCircle, duration: 2000 },
      { id: 'categorizing', text: 'Categorizing operational & market risks...', icon: Layers, duration: 2200 },
      { id: 'analyzing', text: 'Analyzing mitigation strategies...', icon: Shield, duration: 2500 },
      { id: 'crafting', text: 'Drafting comprehensive risk matrix...', icon: Wand2, duration: 2500 },
      { id: 'polishing', text: 'Ensuring thorough risk coverage...', icon: Sparkles, duration: 1500 },
    ],
    // Default stages for sections without specific messages
    default: [
      { id: 'connecting', text: 'Connecting to Neural Proposal Engine...', icon: Database, duration: 1500 },
      { id: 'loading', text: 'Loading your validated business context...', icon: Layers, duration: 1800 },
      { id: 'exploring', text: 'Exploring professional templates...', icon: FileSearch, duration: 2000 },
      { id: 'analyzing', text: 'Analyzing section requirements...', icon: Search, duration: 2200 },
      { id: 'reasoning', text: 'Activating AI Reasoning Module...', icon: Cpu, duration: 2500 },
      { id: 'researching', text: 'Gathering authoritative references...', icon: BookMarked, duration: 2200 },
      { id: 'crafting', text: 'Composing professional content...', icon: Wand2, duration: 2800 },
      { id: 'citations', text: 'Embedding citations & sources...', icon: BookOpen, duration: 1500 },
      { id: 'polishing', text: 'Refining for endorsing body standards...', icon: Sparkles, duration: 1500 },
    ],
  };

  // Get stages for current section
  function getStagesForSection(sectionId) {
    return SECTION_STAGES[sectionId] || SECTION_STAGES.default;
  }

  // Professional tip messages based on section
  const SECTION_TIPS = {
    executive_summary: 'Our Neural Proposal Engine is trained on 10,000+ successful visa applications',
    problem: 'Analyzing market gaps using proprietary industry intelligence',
    solution: 'Benchmarking your innovation against UK tech sector standards',
    investment_plans: 'Calibrating funding requirements to UK endorsing body expectations',
    pricing: 'Modeling pricing against successful UK SaaS benchmarks',
    revenue_forecast: 'Projections aligned with realistic UK market growth rates',
    risk_assessment: 'Comprehensive risk framework built on regulatory best practices',
    default: 'Our professionally trained AI models are crafting your section',
  };

  // Celebration confetti
  function triggerCelebration() {
    const duration = 2000;
    const end = Date.now() + duration;
    
    const colors = ['#dc2626', '#ef4444', '#f97316', '#fbbf24', '#22c55e'];
    
    (function frame() {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: colors
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: colors
      });
      
      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    }());
  }

  // Load section data when section changes
  useEffect(() => {
    if (!selectedSection || !selectedProposal) return;
    
    let isMounted = true;
    
    const loadSection = async () => {
      if (!isMounted) return;
      
      // Don't reset if already generating
      if (generatingContent) return;
      
      // Reset state
      setSectionContent('');
      setSectionCitations([]);
      setSectionSaved(true);
      setSectionApproved(false);
      setGenerationStage('');
      setGenerationProgress(0);
      setTypewriterText('');
      setActiveTab('workspace');
      
      try {
        const res = await axios.get(
          API + '/ai/proposals/' + selectedProposal.id + '/sections/' + selectedSection,
          { headers: { Authorization: 'Bearer ' + token } }
        );
        if (!isMounted) return;
        
        if (res.data.content) {
          // Section already has content - DON'T auto-generate
          setSectionContent(res.data.content);
          setSectionCitations(res.data.citations || []);
          setSectionApproved(res.data.status === 'approved');
          setSectionSaved(true);
        } else {
          // No content - check if section is unlocked before auto-generating
          const idx = TECHNICAL_SECTIONS.findIndex(s => s.id === selectedSection);
          const prevSectionId = idx > 0 ? TECHNICAL_SECTIONS[idx - 1]?.id : null;
          const prevCompleted = idx === 0 || 
            (selectedProposal.completed_sections || []).includes(prevSectionId) ||
            completedSections.includes(prevSectionId);
          if (prevCompleted && isMounted) {
            startAutoGeneration();
          }
        }
      } catch (err) {
        // Section doesn't exist - check if unlocked before auto-generating
        const idx = TECHNICAL_SECTIONS.findIndex(s => s.id === selectedSection);
        const prevSectionId = idx > 0 ? TECHNICAL_SECTIONS[idx - 1]?.id : null;
        const prevCompleted = idx === 0 || 
          (selectedProposal.completed_sections || []).includes(prevSectionId) ||
          completedSections.includes(prevSectionId);
        if (prevCompleted && isMounted) {
          startAutoGeneration();
        }
      }
    };
    
    loadSection();
    
    return () => { isMounted = false; };
  }, [selectedSection, selectedProposal?.id]);

  // Auto-generation function with animations
  async function startAutoGeneration() {
    if (!selectedProposal || !selectedSection || generatingContent) return;
    
    setGeneratingContent(true);
    
    // Get section-specific stages
    const stages = getStagesForSection(selectedSection);
    
    // Run through animation stages
    for (let i = 0; i < stages.length; i++) {
      const stage = stages[i];
      setGenerationStage(stage.id);
      setGenerationProgress(((i + 1) / stages.length) * 90); // Cap at 90% for API call
      await new Promise(resolve => setTimeout(resolve, stage.duration));
    }
    
    // Set final stage for API call
    setGenerationStage('generating');
    setGenerationProgress(95);
    
    // Call backend to generate content
    try {
      const res = await axios.post(
        API + '/ai/proposals/' + selectedProposal.id + '/sections/' + selectedSection + '/auto-generate',
        {},
        { headers: { Authorization: 'Bearer ' + token } }
      );
      
      const content = res.data.content;
      const citations = res.data.citations || [];
      
      // Set 100% and show celebration
      setGenerationProgress(100);
      setGenerationStage('complete');
      
      // Trigger celebration confetti!
      triggerCelebration();
      
      // Short delay to show celebration
      await new Promise(resolve => setTimeout(resolve, 1200));
      
      // Now hide the generating state and show content
      setGeneratingContent(false);
      setGenerationStage('');
      setGenerationProgress(0);
      
      // Set the content
      setSectionContent(content);
      setSectionCitations(citations);
      setCreditsRemaining(res.data.credits_remaining || creditsRemaining);
      setSectionSaved(false);
      
      // Show review modal
      setEditedContent(content);
      setShowReviewModal(true);
      
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error('Insufficient credits to generate content');
      } else {
        toast.error('Failed to generate content. Please try again.');
      }
      setGeneratingContent(false);
      setGenerationStage('');
      setGenerationProgress(0);
    }
  }

  // Typewriter effect
  async function typewriterEffect(text) {
    const words = text.split(' ');
    let currentText = '';
    
    for (let i = 0; i < words.length; i++) {
      currentText += (i > 0 ? ' ' : '') + words[i];
      setTypewriterText(currentText);
      // Variable speed based on punctuation
      const delay = words[i].match(/[.!?]$/) ? 100 : 30;
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  // Approve section and unlock next
  async function handleApproveSection() {
    if (!selectedProposal || !selectedSection) return;
    
    setSectionSaving(true);
    try {
      await axios.put(
        API + '/ai/proposals/' + selectedProposal.id + '/sections/' + selectedSection,
        {
          content: editedContent,
          citations: sectionCitations,
          status: 'approved'
        },
        { headers: { Authorization: 'Bearer ' + token } }
      );
      
      setSectionContent(editedContent);
      setSectionApproved(true);
      setSectionSaved(true);
      setShowReviewModal(false);
      
      // Add to completed sections
      setCompletedSections(prev => [...prev.filter(s => s !== selectedSection), selectedSection]);
      
      // Find and select next section
      const currentIndex = TECHNICAL_SECTIONS.findIndex(s => s.id === selectedSection);
      if (currentIndex < TECHNICAL_SECTIONS.length - 1) {
        const nextSection = TECHNICAL_SECTIONS[currentIndex + 1];
        toast.success(`${currentSection?.title} approved! Moving to ${nextSection.title}...`);
        setTimeout(() => {
          setSelectedSection(nextSection.id);
        }, 1500);
      } else {
        toast.success('All sections completed! Your proposal is ready for review.');
      }
    } catch (err) {
      toast.error('Failed to save section');
    } finally {
      setSectionSaving(false);
    }
  }

  // Regenerate section
  async function handleRegenerateSection() {
    setShowReviewModal(false);
    setSectionContent('');
    setTypewriterText('');
    startAutoGeneration();
  }

  // Auto-save functionality
  const autoSaveTimerRef = useRef(null);
  
  useEffect(() => {
    if (!sectionSaved && sectionContent && selectedProposal && selectedSection && !generatingContent) {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = setTimeout(async () => {
        setSectionSaving(true);
        try {
          await axios.put(
            API + '/ai/proposals/' + selectedProposal.id + '/sections/' + selectedSection,
            { content: sectionContent, citations: sectionCitations },
            { headers: { Authorization: 'Bearer ' + token } }
          );
          setSectionSaved(true);
        } catch (err) {
          console.error('Auto-save failed');
        } finally {
          setSectionSaving(false);
        }
      }, 3000);
    }
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [sectionContent, sectionSaved, selectedProposal?.id, selectedSection, token, generatingContent, sectionCitations]);

  // Manual save with progress toast
  async function handleManualSave() {
    if (!selectedProposal || !selectedSection || sectionSaving) return;
    
    setSectionSaving(true);
    try {
      await axios.put(
        API + '/ai/proposals/' + selectedProposal.id + '/sections/' + selectedSection,
        {
          content: sectionContent,
          citations: sectionCitations
        },
        { headers: { Authorization: 'Bearer ' + token } }
      );
      setSectionSaved(true);
      toast.success('Section saved!');
    } catch (err) {
      toast.error('Failed to save section');
    } finally {
      setSectionSaving(false);
    }
  }

  // Navigate to previous section
  function navigateToPreviousSection() {
    if (!selectedSection) return;
    const currentIndex = TECHNICAL_SECTIONS.findIndex(s => s.id === selectedSection);
    if (currentIndex > 0) {
      const prevSection = TECHNICAL_SECTIONS[currentIndex - 1];
      if (isSectionComplete(prevSection.id) || isSectionUnlocked(prevSection.id)) {
        setSelectedSection(prevSection.id);
      }
    }
  }

  // Navigate to next section
  function navigateToNextSection() {
    if (!selectedSection) return;
    const currentIndex = TECHNICAL_SECTIONS.findIndex(s => s.id === selectedSection);
    if (currentIndex < TECHNICAL_SECTIONS.length - 1) {
      const nextSection = TECHNICAL_SECTIONS[currentIndex + 1];
      if (isSectionComplete(nextSection.id) || isSectionUnlocked(nextSection.id)) {
        setSelectedSection(nextSection.id);
      }
    }
  }

  // Handle wheel scroll for section navigation
  const handleWheelNavigation = useCallback((e) => {
    // Only trigger if scrolled significantly and at top/bottom of content
    if (!workspaceRef.current) return;
    
    const el = workspaceRef.current;
    const atTop = el.scrollTop === 0;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 10;
    
    if (e.deltaY < -50 && atTop) {
      // Scrolling up at top - go to previous section
      e.preventDefault();
      navigateToPreviousSection();
    } else if (e.deltaY > 50 && atBottom) {
      // Scrolling down at bottom - go to next section
      e.preventDefault();
      navigateToNextSection();
    }
  }, [selectedSection]);

  // Export to Word
  async function handleExportWord() {
    if (!selectedProposal) return;
    
    setExportingWord(true);
    try {
      const filename = await exportToWord(selectedProposal, TECHNICAL_SECTIONS, completedSections, selectedTemplate, token, API);
      toast.success(`Exported to ${filename}`);
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Failed to export document');
    } finally {
      setExportingWord(false);
    }
  }

  // Export financial sections to Excel with formulas
  async function handleExportExcel() {
    if (!selectedProposal) return;
    
    setExportingExcel(true);
    try {
      const filename = await exportFinancialExcel(selectedProposal, TECHNICAL_SECTIONS, token, API);
      toast.success(`Financial Plan exported to ${filename}`);
    } catch (err) {
      console.error('Excel export failed:', err);
      toast.error('Failed to export Excel file');
    } finally {
      setExportingExcel(false);
    }
  }

  // Trigger auto-generation manually (for regenerate)
  function triggerRegeneration() {
    setSectionContent('');
    setTypewriterText('');
    setSectionCitations([]);
    startAutoGeneration();
  }

  function handleContentChange(newContent) {
    // Convert HTML from editor to plain text for storage
    const plainText = htmlToText(newContent);
    setSectionContent(plainText);
    setSectionSaved(false);
  }

  function copyContentToClipboard() {
    navigator.clipboard.writeText(sectionContent);
    toast.success('Content copied to clipboard!');
  }

  // AI Section Chat - Send message to AI for section editing help
  async function handleAiChatSend() {
    if (!aiChatInput.trim() || aiChatSending || !selectedSection || !sectionContent) return;
    
    const userMsg = aiChatInput.trim();
    setAiChatMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setAiChatInput('');
    setAiChatSending(true);
    
    try {
      const res = await axios.post(
        API + '/ai/proposals/' + selectedProposal.id + '/sections/' + selectedSection + '/ai-chat',
        { message: userMsg, current_content: sectionContent },
        { headers: { Authorization: 'Bearer ' + token } }
      );
      
      setAiChatMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
      setCreditsRemaining(res.data.credits_remaining);
      
      // Auto-scroll to bottom
      setTimeout(() => {
        if (aiChatScrollRef.current) {
          aiChatScrollRef.current.scrollTop = aiChatScrollRef.current.scrollHeight;
        }
      }, 100);
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error('Insufficient credits. Please purchase more credits.');
      } else {
        toast.error('Failed to send message');
      }
    } finally {
      setAiChatSending(false);
    }
  }

  // Apply AI suggestion to section content
  function applyAiSuggestion(content) {
    setSectionContent(content);
    setSectionSaved(false);
    setIsEditingContent(true);
    toast.success('AI suggestion applied! Review and save your changes.');
  }

  // Load section images from proposals
  useEffect(() => {
    if (selectedProposal && selectedProposal.sections) {
      const images = {};
      for (const sectionId of Object.keys(selectedProposal.sections)) {
        if (selectedProposal.sections[sectionId].image_url) {
          images[sectionId] = selectedProposal.sections[sectionId].image_url;
        }
      }
      setSectionImages(images);
    }
  }, [selectedProposal]);

  // Fetch suggested images for a section
  async function fetchSuggestedImages(sectionId) {
    setSearchingImages(true);
    try {
      const res = await axios.get(API + '/ai/images/suggestions/' + sectionId);
      setImageSearchResults(res.data.images || []);
      setImageSearchQuery(res.data.query || '');
    } catch (err) {
      console.error('Failed to fetch images:', err);
      setImageSearchResults([]);
    } finally {
      setSearchingImages(false);
    }
  }

  // Search images with custom query
  async function searchImages(query) {
    if (!query.trim()) return;
    setSearchingImages(true);
    try {
      const res = await axios.post(API + '/ai/images/search', { query: query.trim() });
      setImageSearchResults(res.data.images || []);
    } catch (err) {
      console.error('Failed to search images:', err);
    } finally {
      setSearchingImages(false);
    }
  }

  // Select image for section
  async function selectSectionImage(imageUrl) {
    if (!selectedSection || !selectedProposal) return;
    
    try {
      await axios.put(
        API + '/ai/proposals/' + selectedProposal.id + '/sections/' + selectedSection + '/image',
        null,
        { 
          headers: { Authorization: 'Bearer ' + token },
          params: { image_url: imageUrl }
        }
      );
      
      setSectionImages(prev => ({ ...prev, [selectedSection]: imageUrl }));
      setShowImageGallery(false);
      toast.success('Image added to section!');
    } catch (err) {
      console.error('Failed to save image:', err);
      toast.error('Failed to save image');
    }
  }

  // Remove image from section
  async function removeSectionImage() {
    if (!selectedSection || !selectedProposal) return;
    
    try {
      await axios.put(
        API + '/ai/proposals/' + selectedProposal.id + '/sections/' + selectedSection + '/image',
        null,
        { 
          headers: { Authorization: 'Bearer ' + token },
          params: { image_url: '' }
        }
      );
      
      setSectionImages(prev => {
        const updated = { ...prev };
        delete updated[selectedSection];
        return updated;
      });
      toast.success('Image removed from section');
    } catch (err) {
      console.error('Failed to remove image:', err);
    }
  }

  // Reset AI chat when section changes
  useEffect(() => {
    setAiChatMessages([]);
    setAiChatInput('');
    setShowAiChatPanel(false);
  }, [selectedSection]);

  async function handleSendMessage() {
    if (!userInput.trim() || sending || !selectedSection) return;
    
    const newMessage = { role: 'user', content: userInput };
    setChatMessages(prev => [...prev, newMessage]);
    setUserInput('');
    setSending(true);

    try {
      const res = await axios.post(API + '/ai/proposals/' + selectedProposal.id + '/chat', { section: selectedSection, message: userInput }, { headers: { Authorization: 'Bearer ' + token } });
      setChatMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  }

  if (loading) {
    return (
      <div className="h-screen bg-slate-100 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-red-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const userName = user?.name || 'User';
  const userEmail = user?.email || '';

  // Pre-build menu items with lock/unlock icons based on package
  const menuElements = [];
  const interviewBadge = interviewFreeAvailable ? 'FREE' : (interviewCredits > 0 ? interviewCredits.toString() : null);
  
  // Determine what's unlocked based on package
  const hasPremiumService = purchases.some(p => p.package_type === 'premium');
  
  // Interview access: has proposal access OR has interview credits
  const hasInterviewAccess = hasProposalAccess || interviewFreeAvailable || interviewCredits > 0;
  
  const menuData = [
    { id: 'video', icon: Video, label: 'Video Course', locked: !hasVideoCourse, showLockIcon: true },
    { id: 'proposals', icon: FileText, label: 'Proposal System', locked: !hasProposalAccess, showLockIcon: true },
    { id: 'interview', icon: Mic, label: 'Mock Interview', locked: !hasInterviewAccess, showLockIcon: true, badge: interviewBadge, badgeColor: interviewFreeAvailable ? 'bg-green-500' : 'bg-red-500' },
    { id: 'questionbank', icon: MessageSquare, label: 'Question Bank', locked: !hasAnyCorePackage, showLockIcon: true },
    { id: 'hire', icon: Briefcase, label: 'Hire Me', locked: !hasPremiumService, showLockIcon: true },
    { id: 'zoom', icon: Calendar, label: 'Book Zoom Call', locked: !hasAnyCorePackage, showLockIcon: true },
    { id: 'credits', icon: CreditCard, label: 'Buy Credits', locked: !hasAnyCorePackage, showLockIcon: true },
    ...(canUpgrade ? [{ id: 'upgrade', icon: Zap, label: 'Upgrade Package', locked: false, showLockIcon: false, special: 'upgrade' }] : []),
    { id: 'billing', icon: Receipt, label: 'Billing', locked: false, showLockIcon: false },
    { id: 'references', icon: BookOpen, label: 'References', locked: !hasAnyCorePackage, showLockIcon: true },
    { id: 'support', icon: HelpCircle, label: 'Support', locked: false, showLockIcon: false },
  ];
  
  for (let i = 0; i < menuData.length; i++) {
    const item = menuData[i];
    const IconComp = item.icon;
    const isActive = activeMenu === item.id;
    const LockIcon = item.locked ? Lock : (item.showLockIcon ? Unlock : null);
    const isUpgradeItem = item.special === 'upgrade';
    
    menuElements.push(
      <button key={item.id} onClick={() => {
        if (isUpgradeItem) {
          setShowUpgradeModal(true);
          return;
        }
        if (item.locked && !hasAnyCorePackage) {
          setShowPackageGate(true);
          return;
        }
        if (!item.locked) {
          handleMenuClick(item.id);
        }
      }}
        className={'w-full px-4 py-3 flex items-center gap-3 text-left transition-colors ' + 
          (isUpgradeItem ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700' :
           isActive ? 'bg-red-600 text-white' : 'text-slate-300 hover:bg-slate-800') + 
          (item.locked ? ' opacity-50 cursor-not-allowed' : '')}>
        <IconComp className="w-5 h-5" />
        <span className="font-medium flex-1">{item.label}</span>
        {isUpgradeItem && <Sparkles className="w-4 h-4 text-yellow-300" />}
        {!isUpgradeItem && item.showLockIcon && LockIcon && <LockIcon className={'w-4 h-4 ' + (item.locked ? 'text-red-400' : 'text-green-400')} />}
        {item.badge && <span className={'text-xs px-1.5 py-0.5 text-white rounded-full ' + (item.badgeColor || 'bg-green-500')}>{item.badge}</span>}
      </button>
    );
  }

  // Pre-build upgrade option elements for modal (avoid babel recursion)
  const upgradeOptionElements = [];
  for (let i = 0; i < upgradeOptions.length; i++) {
    const option = upgradeOptions[i];
    const isBlue = option.color === 'blue';
    
    // Pre-build feature list items
    const featureItems = [];
    for (let j = 0; j < option.features.length; j++) {
      featureItems.push(
        <li key={j} className="flex items-center gap-2">
          <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
          {option.features[j]}
        </li>
      );
    }
    
    upgradeOptionElements.push(
      <div 
        key={option.type}
        className={'border-2 rounded-2xl p-6 transition-all hover:shadow-lg ' + 
          (isBlue 
            ? 'border-blue-200 hover:border-blue-400 bg-gradient-to-br from-blue-50 to-white'
            : 'border-purple-200 hover:border-purple-400 bg-gradient-to-br from-purple-50 to-white'
          )}
      >
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <div className={'w-12 h-12 rounded-xl flex items-center justify-center ' + (isBlue ? 'bg-blue-100' : 'bg-purple-100')}>
                {option.type === 'complete' ? <Rocket className={'w-6 h-6 ' + (isBlue ? 'text-blue-600' : 'text-purple-600')} /> : <Sparkles className="w-6 h-6 text-purple-600" />}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="font-bold text-slate-900 text-lg">{option.name}</h4>
                  {option.highlight && (
                    <Badge className={(isBlue ? 'bg-blue-500' : 'bg-purple-500') + ' text-white text-xs'}>
                      {option.highlight}
                    </Badge>
                  )}
                </div>
                <p className="text-2xl font-bold mt-1 text-slate-900">
                  £{option.price}
                  <span className="text-sm font-normal text-slate-500 ml-1">one-time</span>
                </p>
              </div>
            </div>
            
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-slate-600">
              {featureItems}
            </ul>
          </div>
          
          <div className="flex-shrink-0">
            <Button
              onClick={() => {
                setSelectedPaymentPackage({ 
                  id: option.type, 
                  name: option.name, 
                  price: option.price 
                });
                setShowUpgradeModal(false);
                setShowPaymentModal(true);
              }}
              className={'w-full md:w-auto px-8 py-3 shadow-lg ' + 
                (isBlue 
                  ? 'bg-blue-600 hover:bg-blue-700' 
                  : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700'
                )}
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              Upgrade Now
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Pre-build chapter list
  const chapterElements = [];
  for (let i = 0; i < VIDEO_CHAPTERS.length; i++) {
    const ch = VIDEO_CHAPTERS[i];
    const isSelected = selectedChapter?.id === ch.id;
    chapterElements.push(
      <button key={ch.id} onClick={() => setSelectedChapter(ch)}
        className={'w-full p-4 flex items-start gap-3 text-left border-b border-slate-100 transition-colors ' + (isSelected ? 'bg-red-50 border-l-4 border-l-red-500' : 'hover:bg-slate-50')}>
        <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0 text-sm font-bold text-slate-600">{i + 1}</div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-slate-900 text-sm truncate">{ch.title}</p>
          <p className="text-xs text-slate-500">{ch.duration_minutes} min</p>
        </div>
      </button>
    );
  }

  // Pre-build section list with categories
  const sectionElements = [];
  const categories = ['overview', 'strategy', 'validation', 'location', 'team', 'market', 'marketing', 'financial', 'risk'];
  const catTitles = { overview: 'Overview', strategy: 'Strategy', validation: 'Validation', location: 'Location', team: 'Team', market: 'Market Analysis', marketing: 'Marketing', financial: 'Financial Plan', risk: 'Risk' };
  
  for (let c = 0; c < categories.length; c++) {
    const cat = categories[c];
    const catSections = TECHNICAL_SECTIONS.filter(s => s.category === cat);
    if (catSections.length === 0) continue;
    
    sectionElements.push(
      <div key={'cat-' + cat} className="px-4 py-2 bg-slate-50 border-y border-slate-100">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{catTitles[cat]}</p>
      </div>
    );
    
    for (let s = 0; s < catSections.length; s++) {
      const section = catSections[s];
      const globalIdx = TECHNICAL_SECTIONS.findIndex(x => x.id === section.id);
      const unlocked = isSectionUnlocked(section.id);
      const complete = isSectionComplete(section.id);
      const isActive = selectedSection === section.id;
      
      sectionElements.push(
        <button key={section.id} onClick={() => (unlocked || complete) && setSelectedSection(section.id)} disabled={!unlocked && !complete}
          className={'w-full p-3 flex items-start gap-3 text-left border-b border-slate-50 transition-colors ' + (isActive ? 'bg-red-50 border-l-4 border-l-red-500' : (unlocked || complete) ? 'hover:bg-slate-50' : 'opacity-50 cursor-not-allowed')}>
          <div className={'w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ' + (complete ? 'bg-green-500 text-white' : unlocked ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-400')}>
            {complete ? <Check className="w-3 h-3" /> : unlocked ? (globalIdx + 1) : <Lock className="w-3 h-3" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-slate-900 text-sm">{section.title}</p>
            <p className="text-xs text-slate-500 truncate">{section.description}</p>
          </div>
        </button>
      );
    }
  }

  // Pre-build proposal dropdown
  const proposalDropdownElements = [];
  for (let i = 0; i < proposals.length; i++) {
    const p = proposals[i];
    const isSelected = selectedProposal?.id === p.id;
    const isLocked = p.status === 'idea_locked';
    proposalDropdownElements.push(
      <button key={p.id} onClick={() => { setSelectedProposal(p); setShowProposalDropdown(false); }}
        className={'w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center justify-between ' + (isSelected ? 'bg-red-50 text-red-700' : '')}>
        <div className="flex items-center gap-2">
          <span>{p.idea_title || 'Untitled Proposal'}</span>
          {isLocked && <Lock className="w-3 h-3 text-green-600" />}
        </div>
        <div className="flex items-center gap-2">
          {isLocked && <Badge className="bg-green-100 text-green-700 text-xs py-0">Locked</Badge>}
          {isSelected && <Check className="w-4 h-4" />}
        </div>
      </button>
    );
  }

  // Pre-build chat messages
  const chatElements = [];
  for (let i = 0; i < chatMessages.length; i++) {
    const msg = chatMessages[i];
    const isUser = msg.role === 'user';
    chatElements.push(
      <div key={i} className={'flex items-start gap-3 ' + (isUser ? 'flex-row-reverse' : '')}>
        <div className={'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ' + (isUser ? 'bg-slate-200' : 'bg-red-500')}>
          {isUser ? <User className="w-4 h-4 text-slate-600" /> : <Bot className="w-4 h-4 text-white" />}
        </div>
        <div className={'max-w-[70%] rounded-2xl px-4 py-3 ' + (isUser ? 'bg-red-500 text-white rounded-tr-none' : 'bg-white shadow-sm rounded-tl-none')}>
          <p className="whitespace-pre-wrap">{msg.content}</p>
        </div>
      </div>
    );
  }

  // Pre-build purchases for billing
  const purchaseElements = [];
  for (let i = 0; i < purchases.length; i++) {
    const purchase = purchases[i];
    purchaseElements.push(
      <div key={i} className="bg-slate-50 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="font-medium text-slate-900">{purchase.course?.title || 'Package Purchase'}</span>
          <Badge className="bg-green-100 text-green-700">Paid</Badge>
        </div>
        <p className="text-sm text-slate-500">£{purchase.purchase?.amount || '149.00'}</p>
        <p className="text-xs text-slate-400 mt-1">{new Date(purchase.purchase?.created_at).toLocaleDateString()}</p>
      </div>
    );
  }

  // Pre-build support ticket elements
  const ticketListElements = [];
  for (let i = 0; i < supportTickets.length; i++) {
    const ticket = supportTickets[i];
    const isSelected = selectedTicket?.id === ticket.id;
    const statusClass = ticket.status === 'open' ? 'bg-yellow-100 text-yellow-700' :
      ticket.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
      ticket.status === 'resolved' ? 'bg-green-100 text-green-700' :
      'bg-slate-100 text-slate-700';
    const lastMsg = ticket.messages && ticket.messages.length > 0 ? ticket.messages[ticket.messages.length - 1]?.message : '';
    ticketListElements.push(
      <button
        key={ticket.id}
        onClick={() => { setSelectedTicket(ticket); setShowNewTicketForm(false); }}
        className={`w-full p-4 text-left hover:bg-slate-50 transition-colors ${isSelected ? 'bg-red-50 border-l-4 border-l-red-500' : ''}`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="font-medium text-slate-900 truncate">{ticket.subject}</p>
            <p className="text-sm text-slate-500 truncate">{lastMsg}</p>
          </div>
          <Badge className={`flex-shrink-0 ${statusClass}`}>{ticket.status}</Badge>
        </div>
        <p className="text-xs text-slate-400 mt-1">{new Date(ticket.updated_at).toLocaleDateString()}</p>
      </button>
    );
  }

  // Pre-build ticket message elements
  const ticketMessageElements = [];
  if (selectedTicket && selectedTicket.messages) {
    for (let i = 0; i < selectedTicket.messages.length; i++) {
      const msg = selectedTicket.messages[i];
      const isAdmin = msg.sender === 'admin';
      ticketMessageElements.push(
        <div key={msg.id} className={`flex ${isAdmin ? 'justify-start' : 'justify-end'}`}>
          <div className={`max-w-md rounded-xl p-4 ${isAdmin ? 'bg-slate-200 text-slate-900' : 'bg-red-500 text-white'}`}>
            <p className="text-xs font-medium mb-1 opacity-75">{msg.sender_name}</p>
            <p className="text-sm">{msg.message}</p>
            <p className="text-xs mt-2 opacity-50">{new Date(msg.created_at).toLocaleString()}</p>
          </div>
        </div>
      );
    }
  }

  // Pre-build support ticket cards for full-width layout
  const supportTicketCards = [];
  for (let i = 0; i < supportTickets.length; i++) {
    const ticket = supportTickets[i];
    const isExpanded = selectedTicket?.id === ticket.id;
    const statusClass = ticket.status === 'open' ? 'bg-yellow-100 text-yellow-700' :
      ticket.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
      ticket.status === 'resolved' ? 'bg-green-100 text-green-700' :
      'bg-slate-100 text-slate-700';
    const statusIcon = ticket.status === 'open' ? Clock : 
      ticket.status === 'in_progress' ? Loader2 : 
      ticket.status === 'resolved' ? CheckCircle2 : X;
    const StatusIcon = statusIcon;
    const lastMsg = ticket.messages && ticket.messages.length > 0 ? ticket.messages[ticket.messages.length - 1]?.message : '';
    
    // Build messages for this specific ticket when expanded
    const ticketMsgElements = [];
    if (isExpanded && ticket.messages) {
      for (let j = 0; j < ticket.messages.length; j++) {
        const msg = ticket.messages[j];
        const isAdmin = msg.sender === 'admin';
        ticketMsgElements.push(
          <div key={msg.id} className={`flex ${isAdmin ? 'justify-start' : 'justify-end'}`}>
            <div className={`max-w-md rounded-xl p-3 ${isAdmin ? 'bg-slate-100 text-slate-900' : 'bg-purple-500 text-white'}`}>
              <p className="text-xs font-medium mb-1 opacity-75">{msg.sender_name}</p>
              <p className="text-sm">{msg.message}</p>
              <p className="text-xs mt-1 opacity-50">{new Date(msg.created_at).toLocaleString()}</p>
            </div>
          </div>
        );
      }
    }
    
    supportTicketCards.push(
      <div key={ticket.id} className={`bg-white rounded-2xl border ${isExpanded ? 'border-purple-300 shadow-md' : 'border-slate-200'} overflow-hidden transition-all`}>
        <button
          onClick={() => setSelectedTicket(isExpanded ? null : ticket)}
          className="w-full p-5 text-left hover:bg-slate-50 transition-colors"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4 flex-1 min-w-0">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${ticket.status === 'open' ? 'bg-yellow-100' : ticket.status === 'in_progress' ? 'bg-blue-100' : ticket.status === 'resolved' ? 'bg-green-100' : 'bg-slate-100'}`}>
                <StatusIcon className={`w-5 h-5 ${ticket.status === 'open' ? 'text-yellow-600' : ticket.status === 'in_progress' ? 'text-blue-600' : ticket.status === 'resolved' ? 'text-green-600' : 'text-slate-600'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-900">{ticket.subject}</p>
                <p className="text-sm text-slate-500 truncate mt-1">{lastMsg || 'No messages yet'}</p>
                <div className="flex items-center gap-3 mt-2">
                  <span className="text-xs text-slate-400">{ticket.category}</span>
                  <span className="text-xs text-slate-300">•</span>
                  <span className="text-xs text-slate-400">{new Date(ticket.updated_at).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <Badge className={statusClass}>{ticket.status}</Badge>
              <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
            </div>
          </div>
        </button>
        
        {isExpanded && (
          <div className="border-t border-slate-100">
            <div className="p-5 bg-slate-50 space-y-3 max-h-80 overflow-y-auto">
              {ticketMsgElements.length > 0 ? ticketMsgElements : (
                <p className="text-center text-slate-500 py-4">No messages in this ticket yet</p>
              )}
            </div>
            {ticket.status !== 'closed' && (
              <div className="p-4 border-t border-slate-100 bg-white">
                <div className="flex gap-2">
                  <input 
                    type="text"
                    value={ticketReply}
                    onChange={(e) => setTicketReply(e.target.value)}
                    placeholder="Type your reply..."
                    className="flex-1 px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    onKeyPress={(e) => e.key === 'Enter' && sendTicketReply()}
                  />
                  <Button 
                    onClick={sendTicketReply}
                    disabled={sendingTicketReply || !ticketReply.trim()}
                    className="bg-purple-500 hover:bg-purple-600 px-6"
                  >
                    {sendingTicketReply ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Pre-build available time slots elements
  const slotElements = [];
  for (let i = 0; i < availableSlots.length; i++) {
    const slot = availableSlots[i];
    const isSelected = selectedSlot === slot.time;
    slotElements.push(
      <button
        key={slot.time}
        onClick={() => setSelectedSlot(slot.time)}
        className={`px-3 py-2 text-sm rounded-lg border transition-colors ${isSelected ? 'bg-blue-500 border-blue-500 text-white' : 'border-slate-200 hover:border-blue-300 hover:bg-blue-50'}`}
      >
        {slot.display}
      </button>
    );
  }

  // Pre-build zoom bookings elements
  const bookingElements = [];
  for (let i = 0; i < zoomBookings.length; i++) {
    const booking = zoomBookings[i];
    const borderClass = booking.status === 'confirmed' ? 'border-green-200 bg-green-50' :
      booking.status === 'cancelled' ? 'border-slate-200 bg-slate-50 opacity-60' :
      'border-yellow-200 bg-yellow-50';
    const badgeClass = booking.status === 'confirmed' ? 'bg-green-100 text-green-700' :
      booking.status === 'cancelled' ? 'bg-slate-100 text-slate-700' :
      'bg-yellow-100 text-yellow-700';
    bookingElements.push(
      <div key={booking.id} className={`p-4 rounded-lg border ${borderClass}`}>
        <div className="flex items-start justify-between">
          <div>
            <p className="font-medium text-slate-900">{booking.topic}</p>
            <p className="text-sm text-slate-600">{booking.date} at {booking.time_slot}</p>
            <p className="text-xs text-slate-500 mt-1">{booking.description}</p>
          </div>
          <Badge className={badgeClass}>{booking.status}</Badge>
        </div>
        {booking.zoom_link && (
          <a href={booking.zoom_link} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1 text-blue-600 text-sm hover:underline">
            <Link2 className="w-4 h-4" />
            Join Zoom Call
          </a>
        )}
        {booking.status === 'pending' && (
          <Button 
            size="sm" 
            variant="outline" 
            className="mt-3 text-red-500 border-red-200 hover:bg-red-50"
            onClick={() => cancelZoomBooking(booking.id)}
          >
            Cancel Booking
          </Button>
        )}
      </div>
    );
  }

  // Pre-build Question Bank items (filter out empty questions)
  const filteredQuestionBankItems = questionBankItems.filter(item => item.question && item.question.trim());
  const questionBankElements = [];
  for (let i = 0; i < filteredQuestionBankItems.length; i++) {
    const item = filteredQuestionBankItems[i];
    const isExpanded = selectedQuestionItem?.id === item.id;
    const hasAdminResponse = !!item.admin_response;
    const hasAiResponse = !!item.ai_response;
    const isForwarded = item.forwarded;
    
    questionBankElements.push(
      <div 
        key={item.id || i} 
        className={`bg-white rounded-2xl border ${isExpanded ? 'border-emerald-300 shadow-md' : 'border-slate-200'} overflow-hidden transition-all`}
        data-testid={`question-item-${i}`}
      >
        <button
          onClick={() => setSelectedQuestionItem(isExpanded ? null : item)}
          className="w-full p-5 text-left hover:bg-slate-50 transition-colors"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4 flex-1 min-w-0">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${hasAdminResponse ? 'bg-blue-100' : hasAiResponse ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                {hasAdminResponse ? (
                  <User className="w-5 h-5 text-blue-600" />
                ) : hasAiResponse ? (
                  <Bot className="w-5 h-5 text-emerald-600" />
                ) : (
                  <Clock className="w-5 h-5 text-amber-600" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900 line-clamp-2">{item.question || '(No question text)'}</p>
                {item.chapter_title && (
                  <p className="text-sm text-slate-500 mt-1">
                    <Video className="w-3 h-3 inline mr-1" />
                    {item.chapter_title}
                  </p>
                )}
                <div className="flex items-center gap-3 mt-2">
                  <span className="text-xs text-slate-400">
                    {new Date(item.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                  {isForwarded && (
                    <Badge className="bg-purple-100 text-purple-700 text-xs">Forwarded to Ahmad</Badge>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <Badge className={hasAdminResponse ? 'bg-blue-100 text-blue-700' : hasAiResponse ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                {hasAdminResponse ? 'Ahmad Answered' : hasAiResponse ? 'AI Answered' : 'Pending'}
              </Badge>
              <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
            </div>
          </div>
        </button>
        
        {isExpanded && (
          <div className="border-t border-slate-100">
            {hasAiResponse && (
              <div className="p-5 bg-emerald-50">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-emerald-800 text-sm mb-2">AI Assistant</p>
                    <p className="text-slate-700 whitespace-pre-wrap text-sm">{item.ai_response}</p>
                  </div>
                </div>
              </div>
            )}
            
            {hasAdminResponse && (
              <div className="p-5 bg-blue-50">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-blue-800 text-sm mb-2">Ahmad Raza</p>
                    <p className="text-slate-700 whitespace-pre-wrap text-sm">{item.admin_response}</p>
                  </div>
                </div>
              </div>
            )}
            
            {!hasAdminResponse && !isForwarded && item.type === 'video_comment' && (
              <div className="p-4 bg-slate-50 border-t border-slate-100">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-600">
                    Not satisfied with the AI response? Get Ahmad&apos;s personal guidance.
                  </p>
                  <Button
                    size="sm"
                    onClick={() => forwardCommentToAhmad(item.id)}
                    disabled={forwardingComment === item.id}
                    className="bg-purple-500 hover:bg-purple-600"
                    data-testid={`forward-to-ahmad-btn-${i}`}
                  >
                    {forwardingComment === item.id ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Forwarding...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Forward to Ahmad
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
            
            {isForwarded && !hasAdminResponse && (
              <div className="p-4 bg-purple-50 border-t border-slate-100">
                <div className="flex items-center gap-2 text-purple-700">
                  <Clock className="w-4 h-4" />
                  <p className="text-sm">
                    Forwarded to Ahmad - awaiting response
                    {item.ticket_number && <span className="font-medium ml-1">({item.ticket_number})</span>}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Pre-build billing history elements
  const billingHistoryElements = [];
  for (let i = 0; i < purchases.length; i++) {
    const p = purchases[i];
    const purchaseData = p.purchase || p;
    const packageType = purchaseData.package_type || 'Unknown';
    const packageLabel = packageType === 'complete' ? 'Complete Package' : 
                        packageType === 'video' ? 'Video Course' :
                        packageType === 'bundle' ? 'Premium Bundle' : packageType;
    const amount = packageType === 'complete' ? '£99.99' : 
                  packageType === 'video' ? '£19.99' : 
                  packageType === 'bundle' ? '£199.99' : '£0.00';
    const purchaseDate = purchaseData.created_at || purchaseData.purchased_at;
    billingHistoryElements.push(
      <div key={purchaseData.id || i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
            <Check className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <p className="font-medium text-slate-900">{packageLabel}</p>
            <p className="text-xs text-slate-500">{purchaseDate ? new Date(purchaseDate).toLocaleDateString() : 'Date unknown'}</p>
          </div>
        </div>
        <span className="font-semibold text-slate-900">{amount}</span>
      </div>
    );
  }

  const currentSection = TECHNICAL_SECTIONS.find(s => s.id === selectedSection);

  return (
    <div className="h-screen flex flex-col bg-slate-100" data-testid="user-dashboard">
      {/* Mobile Sidebar Overlay */}
      <div 
        className={`sidebar-overlay ${mobileSidebarOpen ? 'active' : ''}`}
        onClick={() => setMobileSidebarOpen(false)}
      />
      
      {/* Top Navigation Bar */}
      <nav className="bg-white border-b border-slate-200 px-4 lg:px-6 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          {/* Mobile Menu Button */}
          <button 
            className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg block lg:hidden"
            onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6" />
          </button>
          
          <Link to="/" className="flex items-center gap-2 lg:gap-3">
            <div className="w-8 h-8 lg:w-9 lg:h-9 rounded-lg gradient-red flex items-center justify-center">
              <Globe className="w-4 h-4 lg:w-5 lg:h-5 text-white" />
            </div>
            <span className="text-base lg:text-lg font-bold hidden sm:block">
              <span className="text-slate-900">innovator</span>
              <span className="text-gradient-red">foundervisa</span>
            </span>
          </Link>
        </div>
        
        <div className="flex items-center gap-2 lg:gap-5">
          {/* Package Badge - Hidden on mobile */}
          <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-green-50 rounded-full border border-green-200">
            <Check className="w-4 h-4 text-green-600" />
            <span className="text-sm font-medium text-green-700">{packageName}</span>
          </div>
          
          {/* AI Credits - Compact on mobile */}
          <div className="flex items-center gap-1 lg:gap-2 px-2 lg:px-3 py-1.5 bg-slate-100 rounded-full">
            <div className="w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center">
              <span className="text-xs font-bold text-white">{creditsRemaining}</span>
            </div>
            <span className="text-xs lg:text-sm font-medium text-slate-700 hidden sm:block">AI Credits</span>
          </div>
          
          {/* Interview Credits Display - Hidden on small mobile */}
          <div 
            className="hidden sm:flex items-center gap-2 px-2 lg:px-3 py-1.5 bg-red-50 rounded-full border border-red-200 cursor-pointer hover:bg-red-100 transition-colors"
            onClick={() => setShowInterviewPurchaseModal(true)}
            title="Click to purchase interview credits"
          >
            <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center">
              <Mic className="w-3 h-3 text-white" />
            </div>
            <span className="text-xs lg:text-sm font-medium text-red-700">
              {interviewFreeAvailable ? '1 FREE' : interviewCredits}
            </span>
          </div>
          
          {/* Upgrade Package Button - Shows only if user can upgrade */}
          {canUpgrade && (
            <Button 
              size="sm" 
              className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white px-2 lg:px-4 shadow-lg shadow-purple-500/25"
              onClick={() => setShowUpgradeModal(true)}
            >
              <Zap className="w-4 h-4 lg:mr-2" />
              <span className="hidden lg:inline">Upgrade</span>
            </Button>
          )}
          
          {/* Buy Credits - Icon only on mobile */}
          <Button 
            size="sm" 
            className="bg-red-500 hover:bg-red-600 text-white px-2 lg:px-4"
            onClick={() => {
              if (!hasAnyCorePackage) {
                setShowPackageGate(true);
              } else {
                setActiveMenu('credits');
              }
            }}
          >
            <CreditCard className="w-4 h-4 lg:mr-2" />
            <span className="hidden lg:inline">Buy Credits</span>
          </Button>
          
          {/* Notification Bell */}
          <div className="relative" ref={notificationRef}>
            <button 
              onClick={() => {
                setShowNotificationDropdown(!showNotificationDropdown);
                if (!showNotificationDropdown && unreadNotificationCount > 0) {
                  markNotificationsRead();
                }
              }}
              className="relative p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <Bell className="w-5 h-5" />
              {unreadNotificationCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {unreadNotificationCount > 9 ? '9+' : unreadNotificationCount}
                </span>
              )}
            </button>
            
            {showNotificationDropdown && (
              <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
                  <h3 className="font-semibold text-slate-900">Notifications</h3>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="px-4 py-8 text-center text-slate-500">
                      <Bell className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                      <p className="text-sm">No notifications yet</p>
                    </div>
                  ) : (
                    notifications.slice(0, 10).map(notif => (
                      <div 
                        key={notif.id} 
                        className={`px-4 py-3 border-b border-slate-100 hover:bg-slate-50 ${!notif.read ? 'bg-red-50' : ''} group relative`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-slate-900">{notif.title}</p>
                            <p className="text-xs text-slate-600 mt-1">{notif.message}</p>
                            <p className="text-xs text-slate-400 mt-1">{new Date(notif.created_at).toLocaleDateString()}</p>
                          </div>
                          <button
                            onClick={(e) => deleteNotification(notif.id, e)}
                            className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-red-100 text-slate-400 hover:text-red-500 transition-all"
                            title="Delete notification"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
          
          <div className="relative pl-4 border-l border-slate-200" ref={userMenuRef}>
            <button onClick={() => setShowUserMenu(!showUserMenu)} className="flex items-center gap-3 hover:bg-slate-50 rounded-lg px-3 py-2 transition-colors">
              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                <User className="w-4 h-4 text-red-600" />
              </div>
              <div className="text-left">
                <p className="text-sm font-medium text-slate-900">{userName}</p>
                <p className="text-xs text-slate-500">{userEmail}</p>
              </div>
              <ChevronDown className="w-4 h-4 text-slate-400" />
            </button>
            
            {showUserMenu && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50">
                <div className="px-4 py-2 border-b border-slate-100">
                  <p className="text-sm font-medium text-slate-900">{userName}</p>
                  <p className="text-xs text-slate-500">{userEmail}</p>
                </div>
                <button onClick={openProfileModal} className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-3">
                  <UserCircle className="w-4 h-4 text-slate-400" />Profile Settings
                </button>
                <button onClick={openPasswordModal} className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-3">
                  <Key className="w-4 h-4 text-slate-400" />Change Password
                </button>
                <button onClick={openBillingModal} className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-3">
                  <Receipt className="w-4 h-4 text-slate-400" />Billing & Invoices
                </button>
                <button onClick={openAccountSettingsModal} className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-3">
                  <Settings className="w-4 h-4 text-slate-400" />Account Settings
                </button>
                <div className="border-t border-slate-100 mt-2 pt-2">
                  <button onClick={handleLogout} className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3">
                    <LogOut className="w-4 h-4" />Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Main 3-Column Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Column 1: Menu - Sidebar */}
        <div className={`dashboard-sidebar w-56 bg-slate-900 flex-shrink-0 flex flex-col ${mobileSidebarOpen ? 'mobile-open' : ''}`}>
          {/* Close button for mobile */}
          <button 
            className="lg:hidden absolute top-4 right-4 p-2 text-slate-400 hover:text-white"
            onClick={() => setMobileSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
          
          {/* Buy Package CTA - shown when user has no core package */}
          {!hasAnyCorePackage && (
            <button
              onClick={() => { setShowPackageGate(true); setMobileSidebarOpen(false); }}
              className="m-3 p-3 bg-gradient-to-r from-red-500 to-red-600 rounded-xl text-white hover:from-red-600 hover:to-red-700 transition-all shadow-lg"
            >
              <div className="flex items-center gap-2">
                <ShoppingCart className="w-5 h-5" />
                <div className="text-left">
                  <p className="font-bold text-sm">Buy Package</p>
                  <p className="text-xs text-red-100">Unlock all features</p>
                </div>
              </div>
            </button>
          )}
          <div className="flex-1 py-2">{menuElements}</div>
          <div className="p-4 border-t border-slate-700">
            <div className="bg-slate-800 rounded-lg p-3 mb-3">
              <p className="text-xs text-slate-400 mb-1">AI Credits</p>
              <p className="text-2xl font-bold text-white">{creditsRemaining}</p>
              <p className="text-xs text-slate-500 mt-1">for idea discussions</p>
            </div>
            <div 
              className="bg-slate-800 rounded-lg p-3 cursor-pointer hover:bg-slate-700 transition-colors"
              onClick={() => setShowInterviewPurchaseModal(true)}
            >
              <p className="text-xs text-slate-400 mb-1">Interview Credits</p>
              <p className="text-2xl font-bold text-white">
                {interviewFreeAvailable ? '1 FREE' : interviewCredits}
              </p>
              <p className="text-xs text-slate-500 mt-1">for mock interviews</p>
            </div>
          </div>
        </div>

        {/* Column 2: List - Hidden for full-width pages, Mobile optimized */}
        {!['zoom', 'support', 'credits', 'references', 'questionbank'].includes(activeMenu) && (
        <div className={`chapter-sidebar w-80 bg-white border-r border-slate-200 flex-shrink-0 flex flex-col overflow-hidden ${mobileChaptersOpen ? 'mobile-open' : ''}`}>
          {/* Mobile close button */}
          <button 
            className="lg:hidden absolute top-2 right-2 p-2 text-slate-400 hover:text-slate-900 z-10"
            onClick={() => setMobileChaptersOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
          
          {activeMenu === 'video' && (
            <>
              {!hasVideoCourse ? (
                <div className="flex-1 flex flex-col items-center justify-center p-8">
                  <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                    <Lock className="w-10 h-10 text-slate-400" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Video Course Locked</h3>
                  <p className="text-slate-600 text-center mb-6 max-w-md">
                    Purchase any package to unlock the comprehensive video course.
                  </p>
                  <Button onClick={() => setShowPackageGate(true)} className="bg-red-500 hover:bg-red-600">
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    View Packages
                  </Button>
                </div>
              ) : (
              <>
              <div className="p-4 border-b border-slate-200">
                <h2 className="font-bold text-slate-900">Video Chapters</h2>
                <p className="text-xs text-slate-500">{VIDEO_CHAPTERS.length} chapters</p>
              </div>
              <div className="flex-1 overflow-y-auto">{chapterElements}</div>
              </>
              )}
            </>
          )}

          {activeMenu === 'proposals' && (
            <>
              {/* Show locked message if user doesn't have proposal access */}
              {!hasProposalAccess ? (
                <div className="flex-1 flex flex-col items-center justify-center p-8">
                  <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                    <Lock className="w-10 h-10 text-slate-400" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Proposal System Locked</h3>
                  <p className="text-slate-600 text-center mb-6 max-w-md">
                    Purchase the Complete System or Premium package to unlock the AI-powered Proposal Builder.
                  </p>
                  <Button onClick={() => setShowPackageGate(true)} className="bg-red-500 hover:bg-red-600">
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    View Packages
                  </Button>
                </div>
              ) : (
              <>
              {/* Proposal Selector at Top of Column 2 */}
              <div className="p-4 border-b border-slate-200 bg-slate-50">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Active Idea</p>
                  <Button size="sm" onClick={handleCreateProposal} className="h-7 text-xs bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700" data-testid="new-proposal-btn">
                    <Plus className="w-3 h-3 mr-1" />New
                  </Button>
                </div>
                
                {proposals.length > 0 ? (
                  <div className="relative">
                    <button 
                      onClick={() => setShowProposalDropdown(!showProposalDropdown)}
                      className="w-full flex items-center justify-between gap-2 p-3 bg-white border border-slate-200 rounded-xl hover:border-red-300 transition-colors shadow-sm"
                      data-testid="proposal-dropdown-trigger"
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        {selectedProposal?.status === 'idea_locked' && <Lock className="w-4 h-4 text-green-600 flex-shrink-0" />}
                        <span className="font-medium text-slate-900 truncate">{selectedProposal?.idea_title || 'Select Idea'}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {selectedProposal?.status === 'idea_locked' && (
                          <Badge className="bg-green-100 text-green-700 text-xs py-0">Locked</Badge>
                        )}
                        <ChevronDown className={'w-4 h-4 text-slate-400 transition-transform ' + (showProposalDropdown ? 'rotate-180' : '')} />
                      </div>
                    </button>
                    
                    {showProposalDropdown && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg z-20 overflow-hidden">
                        {proposalDropdownElements}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-slate-500 mb-2">No ideas yet</p>
                    <Button size="sm" onClick={handleCreateProposal} className="bg-gradient-to-r from-red-500 to-red-600">
                      <Sparkles className="w-3 h-3 mr-1" />Start AI Idea Engine
                    </Button>
                  </div>
                )}
              </div>

              {/* Section List */}
              <div className="p-4 border-b border-slate-200">
                <h2 className="font-bold text-slate-900 text-sm">Proposal Sections</h2>
                <p className="text-xs text-slate-500">Complete each section in order</p>
              </div>
              <div className="flex-1 overflow-y-auto">{sectionElements}</div>
            </>
              )}
            </>
          )}

          {activeMenu === 'billing' && (
            <>
              <div className="p-4 border-b border-slate-200">
                <h2 className="font-bold text-slate-900">Billing & Invoices</h2>
                <p className="text-xs text-slate-500">Your payment history</p>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                {purchases.length > 0 ? <div className="space-y-3">{purchaseElements}</div> : (
                  <div className="text-center py-8">
                    <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No invoices yet</p>
                  </div>
                )}
              </div>
            </>
          )}


          {activeMenu === 'interview' && (
            <>
              <div className="p-4 border-b border-slate-200">
                <h2 className="font-bold text-slate-900">Mock Interview Practice</h2>
                <p className="text-xs text-slate-500">Practice for your endorsement body interview</p>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                <div className="bg-gradient-to-br from-red-50 to-amber-50 rounded-xl p-6 border border-red-100 mb-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center">
                      <Mic className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900">Endorsement Interview Simulator</h3>
                      <p className="text-sm text-slate-600">Voice-based Professional Interview</p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-700 mb-4">
                    Practice your endorsement body interview with our AI simulator. Answer 10 questions via voice recording and get detailed AI-powered feedback.
                  </p>
                  
                  {/* Interview Info */}
                  <div className="bg-white/80 rounded-lg p-4 mb-4 border border-red-100">
                    <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-red-500" />
                      Interview Format
                    </h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        <span className="text-slate-600">10 Questions</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        <span className="text-slate-600">30 min total</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        <span className="text-slate-600">Voice responses</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        <span className="text-slate-600">AI scoring</span>
                      </div>
                    </div>
                  </div>

                  {/* Pricing */}
                  <div className="flex items-center justify-between bg-slate-900 rounded-lg p-3 mb-4">
                    <div>
                      <span className="text-white font-semibold">Interview Session</span>
                      {interviewFreeAvailable && (
                        <span className="ml-2 px-2 py-0.5 bg-green-500 text-white rounded text-xs">1 FREE</span>
                      )}
                      {!interviewFreeAvailable && interviewCredits > 0 && (
                        <span className="ml-2 px-2 py-0.5 bg-blue-500 text-white rounded text-xs">{interviewCredits} Credit{interviewCredits > 1 ? 's' : ''}</span>
                      )}
                    </div>
                    <span className="text-xl font-bold text-white">£9.99</span>
                  </div>

                  {/* No credits warning */}
                  {!interviewFreeAvailable && interviewCredits === 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                      <div className="flex items-center gap-2 text-amber-700 text-sm">
                        <AlertCircle className="w-4 h-4" />
                        <span>No interview credits available. Purchase a credit to start.</span>
                      </div>
                    </div>
                  )}

                  <Button 
                    className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700"
                    onClick={handleStartMockInterview}
                    data-testid="start-mock-interview-btn"
                  >
                    <Mic className="w-4 h-4 mr-2" />
                    {interviewFreeAvailable || interviewCredits > 0 ? 'Start Mock Interview' : 'Purchase Credit to Start'}
                  </Button>
                </div>
                
                {/* How it Works */}
                <div className="bg-white rounded-xl p-4 border border-slate-200">
                  <h4 className="font-semibold text-slate-900 mb-4">How It Works</h4>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center text-red-600 font-bold text-sm flex-shrink-0">1</div>
                      <div>
                        <p className="font-medium text-slate-900">Mic Test</p>
                        <p className="text-xs text-slate-500">Check your audio is working</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center text-red-600 font-bold text-sm flex-shrink-0">2</div>
                      <div>
                        <p className="font-medium text-slate-900">Read Question (30s)</p>
                        <p className="text-xs text-slate-500">Prepare your answer</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center text-red-600 font-bold text-sm flex-shrink-0">3</div>
                      <div>
                        <p className="font-medium text-slate-900">Answer (3 min)</p>
                        <p className="text-xs text-slate-500">Speak your response</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center text-red-600 font-bold text-sm flex-shrink-0">4</div>
                      <div>
                        <p className="font-medium text-slate-900">AI Assessment</p>
                        <p className="text-xs text-slate-500">Get detailed scoring & tips</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        )}

        {/* Column 3: Content */}
        <div className="flex-1 flex flex-col overflow-hidden bg-slate-50 video-content-area">
          {/* Mobile Toggle Button for Chapters */}
          {activeMenu === 'video' && hasVideoCourse && (
            <button 
              className="mobile-chapter-toggle lg:hidden items-center gap-2"
              onClick={() => setMobileChaptersOpen(!mobileChaptersOpen)}
            >
              <List className="w-5 h-5" />
              <span>Chapters</span>
            </button>
          )}
          
          {activeMenu === 'video' && (
            <div className="flex-1 p-4 lg:p-6 overflow-y-auto">
              {selectedChapter ? (
                <div>
                  {/* Bunny Stream Video Player */}
                  <div className="bg-black rounded-xl aspect-video overflow-hidden mb-4 relative course-video-container">
                    {selectedChapter.video_url ? (
                      <iframe
                        ref={videoIframeRef}
                        id="bunny-video-player"
                        src={selectedChapter.video_url + '?autoplay=false&preload=true&responsive=true'}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full border-0"
                        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
                        allowFullScreen={true}
                        title={selectedChapter.title}
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center bg-slate-900">
                        <div className="text-center">
                          <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-3">
                            <Video className="w-8 h-8 text-slate-500" />
                          </div>
                          <p className="text-slate-400">Video coming soon</p>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Video Navigation */}
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-lg lg:text-xl font-bold text-slate-900">{selectedChapter.title}</h2>
                      <p className="text-slate-500 text-sm">Chapter {VIDEO_CHAPTERS.findIndex(c => c.id === selectedChapter.id) + 1} of {VIDEO_CHAPTERS.length} • {selectedChapter.duration_minutes} minutes</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        data-testid="video-prev-btn"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const currentIndex = VIDEO_CHAPTERS.findIndex(c => c.id === selectedChapter.id);
                          if (currentIndex > 0) {
                            setSelectedChapter(VIDEO_CHAPTERS[currentIndex - 1]);
                          }
                        }}
                        disabled={VIDEO_CHAPTERS.findIndex(c => c.id === selectedChapter.id) === 0}
                        className="gap-1"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        <span className="hidden sm:inline">Previous</span>
                      </Button>
                      <Button
                        data-testid="video-next-btn"
                        size="sm"
                        onClick={() => {
                          const currentIndex = VIDEO_CHAPTERS.findIndex(c => c.id === selectedChapter.id);
                          if (currentIndex < VIDEO_CHAPTERS.length - 1) {
                            setSelectedChapter(VIDEO_CHAPTERS[currentIndex + 1]);
                          }
                        }}
                        disabled={VIDEO_CHAPTERS.findIndex(c => c.id === selectedChapter.id) === VIDEO_CHAPTERS.length - 1}
                        className="gap-1 bg-red-500 hover:bg-red-600"
                      >
                        <span className="hidden sm:inline">Next</span>
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Comments Section */}
                  <VideoComments 
                    chapterId={selectedChapter.id} 
                    chapterTitle={selectedChapter.title}
                    token={token}
                  />
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="w-20 h-20 bg-slate-200 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Video className="w-10 h-10 text-slate-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-700 mb-2">Select a Chapter</h3>
                    <p className="text-slate-500">Choose a chapter from the list</p>
                    {/* Mobile hint */}
                    <button 
                      className="lg:hidden mt-4 px-4 py-2 bg-red-500 text-white rounded-lg"
                      onClick={() => setMobileChaptersOpen(true)}
                    >
                      <List className="w-4 h-4 inline mr-2" />
                      View Chapters
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeMenu === 'proposals' && (
            <>
              {selectedSection && selectedProposal ? (
                <div className="flex-1 flex flex-col overflow-hidden">
                  {/* Compact Header with Navigation and Tools */}
                  <div className="bg-white border-b border-slate-200 px-6 py-3">
                    <div className="flex items-center justify-between">
                      {/* Section Navigation */}
                      <div className="flex items-center gap-4">
                        <button 
                          onClick={navigateToPreviousSection}
                          disabled={TECHNICAL_SECTIONS.findIndex(s => s.id === selectedSection) === 0}
                          className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          title="Previous Section"
                        >
                          <ChevronLeft className="w-5 h-5 text-slate-600" />
                        </button>
                        
                        <div className="text-center min-w-[200px]">
                          <h2 className="font-bold text-slate-900">{currentSection?.title}</h2>
                          <p className="text-xs text-slate-500">
                            Section {TECHNICAL_SECTIONS.findIndex(s => s.id === selectedSection) + 1} of {TECHNICAL_SECTIONS.length}
                          </p>
                        </div>
                        
                        <button 
                          onClick={navigateToNextSection}
                          disabled={TECHNICAL_SECTIONS.findIndex(s => s.id === selectedSection) === TECHNICAL_SECTIONS.length - 1}
                          className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          title="Next Section"
                        >
                          <ChevronRight className="w-5 h-5 text-slate-600" />
                        </button>
                      </div>

                      {/* Tools */}
                      <div className="flex items-center gap-2">
                        {/* Save Status */}
                        <div className="flex items-center gap-2 text-xs mr-2">
                          {sectionSaving ? (
                            <span className="flex items-center gap-1 text-amber-600">
                              <Loader2 className="w-3 h-3 animate-spin" />Saving...
                            </span>
                          ) : sectionSaved ? (
                            <span className="flex items-center gap-1 text-green-600">
                              <Check className="w-3 h-3" />Saved
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-slate-400">
                              <RefreshCw className="w-3 h-3" />Unsaved
                            </span>
                          )}
                        </div>

                        <Button size="sm" variant="outline" onClick={handleManualSave} disabled={sectionSaving || sectionSaved} className="h-8">
                          <Save className="w-3 h-3 mr-1" />Save
                        </Button>

                        {/* Template Selector */}
                        <div className="relative">
                          <Button size="sm" variant="outline" onClick={() => setShowTemplateSelector(!showTemplateSelector)} className="h-8">
                            <Palette className="w-3 h-3 mr-1" />Template
                            <ChevronDown className="w-3 h-3 ml-1" />
                          </Button>
                          {showTemplateSelector && (
                            <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-200 p-4 z-50">
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="font-semibold text-slate-800 text-sm">Choose Template</h4>
                                <button onClick={() => setShowTemplateSelector(false)} className="text-slate-400 hover:text-slate-600">
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                              <TemplateSelector selectedTemplate={selectedTemplate} onSelect={(id) => { setSelectedTemplate(id); setShowTemplateSelector(false); toast.success('Template applied!'); }} />
                            </div>
                          )}
                        </div>

                        <Button size="sm" variant="outline" onClick={() => setShowDocumentPreview(true)} className="h-8">
                          <Eye className="w-3 h-3 mr-1" />Preview
                        </Button>

                        <Button size="sm" variant="outline" onClick={handleExportWord} disabled={exportingWord} className="h-8">
                          {exportingWord ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FileDown className="w-3 h-3 mr-1" />}
                          Word
                        </Button>

                        <Button size="sm" variant="outline" onClick={handleExportExcel} disabled={exportingExcel} className="h-8 text-green-700 border-green-300 hover:bg-green-50" data-testid="export-excel-btn">
                          {exportingExcel ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Table2 className="w-3 h-3 mr-1" />}
                          Excel
                        </Button>

                        <button onClick={() => setShowVideoModal(true)} className="flex items-center gap-1 px-2 py-1.5 bg-red-50 hover:bg-red-100 rounded-lg transition-colors">
                          <Video className="w-3 h-3 text-red-500" />
                          <span className="text-xs font-medium text-red-600">Guide</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Main Content Area with AI Chat Panel */}
                  <div className="flex-1 flex flex-row overflow-hidden">
                    {/* Main Content Area - Single Scrollable View */}
                    <div 
                      ref={workspaceRef}
                      onWheel={handleWheelNavigation}
                      className="flex-1 overflow-y-auto bg-gradient-to-br from-slate-50 to-slate-100 p-6"
                    >
                    {generatingContent ? (
                      /* Enhanced Generation Animation */
                      <div className="h-full flex flex-col items-center justify-center min-h-[500px]">
                        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center max-w-md">
                          
                          {generationStage === 'complete' ? (
                            /* Celebration State */
                            <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{ type: "spring", duration: 0.5 }}
                            >
                              <div className="w-28 h-28 mx-auto rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center shadow-xl shadow-green-200 mb-6">
                                <PartyPopper className="w-14 h-14 text-white" />
                              </div>
                              <h3 className="text-2xl font-bold text-green-600 mb-2">Section Generated!</h3>
                              <p className="text-slate-500">Your professional content is ready for review</p>
                            </motion.div>
                          ) : (
                            /* Progress Animation */
                            <>
                              <div className="relative mb-8">
                                {/* Pulsing outer ring */}
                                <motion.div
                                  animate={{ scale: [1, 1.1, 1], opacity: [0.5, 0.2, 0.5] }}
                                  transition={{ duration: 2, repeat: Infinity }}
                                  className="absolute inset-0 rounded-full bg-red-200"
                                  style={{ transform: 'scale(1.3)' }}
                                />
                                
                                {/* Main icon container */}
                                <motion.div
                                  animate={{ rotate: 360 }}
                                  transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                                  className="relative w-28 h-28 mx-auto rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-xl shadow-red-200"
                                >
                                  {/* Dynamic icon based on stage */}
                                  {(() => {
                                    const stages = getStagesForSection(selectedSection);
                                    const stage = stages.find(s => s.id === generationStage);
                                    const IconComponent = stage?.icon || Brain;
                                    return <IconComponent className="w-12 h-12 text-white" />;
                                  })()}
                                </motion.div>
                              </div>
                              
                              {/* Stage text with animation */}
                              <motion.div 
                                key={generationStage} 
                                initial={{ y: 20, opacity: 0 }} 
                                animate={{ y: 0, opacity: 1 }}
                                exit={{ y: -20, opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                className="mb-6"
                              >
                                <h3 className="text-xl font-bold text-slate-800 mb-1">
                                  {(() => {
                                    const stages = getStagesForSection(selectedSection);
                                    const stage = stages.find(s => s.id === generationStage);
                                    if (generationStage === 'generating') {
                                      return 'Finalizing your content...';
                                    }
                                    return stage?.text || 'Initializing Neural Proposal Engine...';
                                  })()}
                                </h3>
                                <p className="text-sm text-slate-500">
                                  {(() => {
                                    const stages = getStagesForSection(selectedSection);
                                    const stageIdx = stages.findIndex(s => s.id === generationStage);
                                    if (generationStage === 'generating') {
                                      return `Completing generation...`;
                                    }
                                    return `Step ${stageIdx + 1} of ${stages.length}`;
                                  })()}
                                </p>
                              </motion.div>
                              
                              {/* Progress bar */}
                              <div className="w-72 mx-auto">
                                <div className="h-2 bg-slate-200 rounded-full overflow-hidden mb-2">
                                  <motion.div 
                                    initial={{ width: 0 }} 
                                    animate={{ width: generationProgress + '%' }} 
                                    transition={{ duration: 0.5 }}
                                    className="h-full bg-gradient-to-r from-red-500 via-orange-500 to-red-600 rounded-full"
                                  />
                                </div>
                                <p className="text-xs text-slate-400">{Math.round(generationProgress)}% complete</p>
                              </div>
                              
                              {/* Professional tip while waiting */}
                              <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 2 }}
                                className="mt-8 p-4 bg-white rounded-xl border border-slate-200 shadow-sm max-w-sm"
                              >
                                <p className="text-xs text-slate-600 flex items-center gap-2">
                                  <Brain className="w-4 h-4 text-red-500 flex-shrink-0" />
                                  <span>{SECTION_TIPS[selectedSection] || SECTION_TIPS.default}</span>
                                </p>
                              </motion.div>
                            </>
                          )}
                        </motion.div>
                      </div>
                    ) : sectionContent || typewriterText ? (
                      /* Content Display Card with Inline Editing */
                      <div className="max-w-4xl mx-auto">
                        <motion.div 
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden"
                        >
                          {/* Card Header */}
                          <div className="px-8 py-5 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-slate-50 to-white">
                            <div className="flex items-center gap-4">
                              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${sectionApproved ? 'bg-green-500' : 'bg-gradient-to-br from-red-500 to-red-600'}`}>
                                {sectionApproved ? <CheckCircle2 className="w-6 h-6 text-white" /> : <FileText className="w-6 h-6 text-white" />}
                              </div>
                              <div>
                                <h3 className="text-xl font-bold text-slate-900">{currentSection?.title}</h3>
                                <p className="text-sm text-slate-500">{sectionApproved ? 'Approved & Saved' : 'Draft - Click Edit to modify'}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {/* Add Image Button */}
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={() => {
                                  setShowImageGallery(true);
                                  fetchSuggestedImages(selectedSection);
                                }} 
                                className="text-slate-600"
                                data-testid="add-image-btn"
                              >
                                <ImagePlus className="w-4 h-4 mr-1" />
                                {sectionImages[selectedSection] ? 'Change Image' : 'Add Image'}
                              </Button>
                              {/* AI Assistant Button */}
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={() => setShowAiChatPanel(!showAiChatPanel)} 
                                className={`text-slate-600 ${showAiChatPanel ? 'bg-red-50 border-red-300' : ''}`}
                                data-testid="ai-assistant-btn"
                              >
                                <Bot className="w-4 h-4 mr-1" />AI Assistant
                              </Button>
                              {!isEditingContent ? (
                                <Button size="sm" variant="outline" onClick={() => setIsEditingContent(true)} className="text-slate-600">
                                  <Edit3 className="w-4 h-4 mr-1" />Edit
                                </Button>
                              ) : (
                                <Button size="sm" variant="outline" onClick={() => setIsEditingContent(false)} className="text-slate-600">
                                  <Check className="w-4 h-4 mr-1" />Done Editing
                                </Button>
                              )}
                              {!sectionApproved && (
                                <Button onClick={() => { setEditedContent(sectionContent); setShowReviewModal(true); }} className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700">
                                  <Check className="w-4 h-4 mr-2" />Approve & Next
                                </Button>
                              )}
                            </div>
                          </div>

                          {/* Content Area */}
                          <div className="p-8">
                            {/* Section Image Display */}
                            {sectionImages[selectedSection] && (
                              <div className="mb-6 relative group">
                                <img 
                                  src={sectionImages[selectedSection]} 
                                  alt={`Image for ${TECHNICAL_SECTIONS.find(s => s.id === selectedSection)?.title || 'section'}`}
                                  className="w-full max-h-[300px] object-cover rounded-xl shadow-md"
                                />
                                <button
                                  onClick={removeSectionImage}
                                  className="absolute top-3 right-3 p-2 bg-red-500 hover:bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="Remove image"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                            
                            {isEditingContent ? (
                              <RichTextEditor
                                content={textToHtml(sectionContent)}
                                onChange={handleContentChange}
                                placeholder="Start writing..."
                                className="min-h-[400px]"
                              />
                            ) : (
                              <div className="prose prose-slate max-w-none">
                                {typewriterText && isTyping ? (
                                  <p className="whitespace-pre-wrap text-slate-700 leading-relaxed text-lg">
                                    {typewriterText}
                                    <span className="inline-block w-0.5 h-5 bg-red-500 ml-1 animate-pulse" />
                                  </p>
                                ) : (
                                  <div 
                                    className="whitespace-pre-wrap text-slate-700 leading-relaxed text-lg"
                                    dangerouslySetInnerHTML={{ __html: sectionContent.replace(/\n/g, '<br/>') }}
                                  />
                                )}
                              </div>
                            )}
                          </div>

                          {/* Citations */}
                          {sectionCitations.length > 0 && (
                            <div className="px-8 py-5 bg-slate-50 border-t border-slate-100">
                              <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                                <BookOpen className="w-4 h-4" />References
                              </h4>
                              <div className="space-y-1">
                                {(() => {
                                  const cites = [];
                                  for (let i = 0; i < sectionCitations.length; i++) {
                                    cites.push(
                                      <p key={i} className="text-sm text-slate-600">
                                        <span className="font-medium text-red-600">[{i + 1}]</span> {sectionCitations[i]}
                                      </p>
                                    );
                                  }
                                  return cites;
                                })()}
                              </div>
                            </div>
                          )}

                          {/* Footer with Regenerate Option */}
                          {sectionApproved && (
                            <div className="px-8 py-4 border-t border-slate-100 flex justify-between items-center bg-green-50">
                              <span className="text-sm text-green-700 flex items-center gap-2">
                                <CheckCircle2 className="w-4 h-4" />
                                This section is approved and included in your document
                              </span>
                              <Button variant="ghost" size="sm" onClick={triggerRegeneration} className="text-slate-500 hover:text-slate-700">
                                <RefreshCw className="w-4 h-4 mr-1" />Regenerate
                              </Button>
                            </div>
                          )}
                        </motion.div>

                        {/* Scroll Navigation Hint */}
                        <div className="mt-6 text-center">
                          <p className="text-xs text-slate-400 flex items-center justify-center gap-2">
                            <ChevronUp className="w-3 h-3" />
                            Scroll or use arrows to navigate between sections
                            <ChevronDown className="w-3 h-3" />
                          </p>
                        </div>
                      </div>
                    ) : (
                      /* Empty State - Generate Content */
                      <div className="h-full flex flex-col items-center justify-center min-h-[500px]">
                        <div className="text-center max-w-md">
                          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center mx-auto mb-6">
                            <Sparkles className="w-10 h-10 text-slate-400" />
                          </div>
                          <h3 className="text-xl font-bold text-slate-800 mb-2">Generate {currentSection?.title}</h3>
                          <p className="text-slate-500 mb-6">
                            Click below to auto-generate professional content for this section based on your validated business idea.
                          </p>
                          <Button onClick={startAutoGeneration} disabled={generatingContent} className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 px-8 py-3">
                            <Sparkles className="w-5 h-5 mr-2" />Generate Content
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* AI Chat Side Panel */}
                  <AnimatePresence>
                    {showAiChatPanel && sectionContent && (
                      <motion.div
                        initial={{ width: 0, opacity: 0 }}
                        animate={{ width: 380, opacity: 1 }}
                        exit={{ width: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="flex-shrink-0 bg-white border-l border-slate-200 flex flex-col overflow-hidden"
                        data-testid="ai-chat-panel"
                      >
                        {/* Panel Header */}
                        <div className="px-4 py-3 border-b border-slate-200 bg-gradient-to-r from-red-500 to-red-600 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                              <Bot className="w-4 h-4 text-white" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-white text-sm">AI Assistant</h3>
                              <p className="text-xs text-white/70">1 credit per message</p>
                            </div>
                          </div>
                          <button 
                            onClick={() => setShowAiChatPanel(false)}
                            className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
                          >
                            <X className="w-4 h-4 text-white" />
                          </button>
                        </div>
                        
                        {/* Chat Messages */}
                        <div 
                          ref={aiChatScrollRef}
                          className="flex-1 overflow-y-auto p-4 space-y-4"
                        >
                          {aiChatMessages.length === 0 ? (
                            <div className="text-center py-8">
                              <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                                <Sparkles className="w-7 h-7 text-red-500" />
                              </div>
                              <h4 className="font-semibold text-slate-800 mb-2">Ask AI to improve this section</h4>
                              <p className="text-sm text-slate-500 mb-4">Try prompts like:</p>
                              <div className="space-y-2">
                                {(() => {
                                  const suggestions = [
                                    "Make it more compelling",
                                    "Add more statistics",
                                    "Shorten this section",
                                    "Make it sound more professional"
                                  ];
                                  const suggestionElements = [];
                                  for (let i = 0; i < suggestions.length; i++) {
                                    suggestionElements.push(
                                      <button
                                        key={i}
                                        onClick={() => setAiChatInput(suggestions[i])}
                                        className="block w-full text-left text-xs px-3 py-2 bg-slate-50 hover:bg-slate-100 rounded-lg text-slate-600 transition-colors"
                                      >
                                        &ldquo;{suggestions[i]}&rdquo;
                                      </button>
                                    );
                                  }
                                  return suggestionElements;
                                })()}
                              </div>
                            </div>
                          ) : (
                            (() => {
                              const messageElements = [];
                              for (let i = 0; i < aiChatMessages.length; i++) {
                                const msg = aiChatMessages[i];
                                const isUser = msg.role === 'user';
                                messageElements.push(
                                  <div key={i} className={`flex items-start gap-2 ${isUser ? 'flex-row-reverse' : ''}`}>
                                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${isUser ? 'bg-slate-200' : 'bg-red-500'}`}>
                                      {isUser ? <User className="w-3.5 h-3.5 text-slate-600" /> : <Bot className="w-3.5 h-3.5 text-white" />}
                                    </div>
                                    <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${isUser ? 'bg-red-500 text-white rounded-tr-none' : 'bg-slate-100 text-slate-700 rounded-tl-none'}`}>
                                      <p className="whitespace-pre-wrap">{msg.content}</p>
                                      {!isUser && msg.content.length > 100 && (
                                        <button
                                          onClick={() => applyAiSuggestion(msg.content)}
                                          className="mt-2 text-xs text-red-600 hover:text-red-700 font-medium flex items-center gap-1"
                                        >
                                          <Wand2 className="w-3 h-3" />Apply to Section
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                );
                              }
                              return messageElements;
                            })()
                          )}
                          
                          {aiChatSending && (
                            <div className="flex items-start gap-2">
                              <div className="w-7 h-7 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                                <Bot className="w-3.5 h-3.5 text-white" />
                              </div>
                              <div className="bg-slate-100 rounded-xl rounded-tl-none px-3 py-2">
                                <div className="flex items-center gap-2">
                                  <Loader2 className="w-4 h-4 text-red-500 animate-spin" />
                                  <span className="text-sm text-slate-500">Thinking...</span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Credits Display */}
                        <div className="px-4 py-2 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
                          <span className="text-xs text-slate-500">Credits remaining:</span>
                          <span className="text-xs font-semibold text-slate-700">{creditsRemaining}</span>
                        </div>
                        
                        {/* Chat Input */}
                        <div className="p-3 border-t border-slate-200 bg-white">
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={aiChatInput}
                              onChange={(e) => setAiChatInput(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && handleAiChatSend()}
                              placeholder="Ask AI to improve this section..."
                              className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                              disabled={aiChatSending}
                              data-testid="ai-chat-input"
                            />
                            <Button 
                              onClick={handleAiChatSend} 
                              disabled={!aiChatInput.trim() || aiChatSending}
                              className="bg-red-500 hover:bg-red-600 px-3"
                              data-testid="ai-chat-send"
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  </div>
                </div>
              ) : (
                /* No section selected */
                <div className="flex-1 flex items-center justify-center p-8">
                  <div className="text-center max-w-md">
                    <div className="w-20 h-20 bg-slate-200 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FileText className="w-10 h-10 text-slate-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-700 mb-2">
                      {proposals.length === 0 ? 'Start Your Business Plan' : 'Select a Section'}
                    </h3>
                    <p className="text-slate-500 mb-4">
                      {proposals.length === 0 
                        ? 'Use the AI Idea Engine to discover and lock your business idea, then build your proposal section by section.'
                        : 'Choose a section from the list to start building your proposal.'}
                    </p>
                    {proposals.length === 0 && (
                      <Button onClick={handleCreateProposal} className="btn-primary bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700" data-testid="create-first-proposal-btn">
                        <Sparkles className="w-4 h-4 mr-2" />Launch AI Idea Engine
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {activeMenu === 'billing' && (
            <div className="flex-1 p-6 overflow-y-auto">
              <div className="max-w-2xl">
                <h2 className="text-xl font-bold text-slate-900 mb-6">Billing Summary</h2>
                <div className="grid grid-cols-3 gap-4 mb-8">
                  <div className="bg-white rounded-xl p-5 border border-slate-200">
                    <p className="text-sm text-slate-500 mb-1">Current Package</p>
                    <p className="text-lg font-bold text-slate-900">{packageName}</p>
                  </div>
                  <div className="bg-white rounded-xl p-5 border border-slate-200">
                    <p className="text-sm text-slate-500 mb-1">Idea Credits</p>
                    <p className="text-lg font-bold text-slate-900">{ideaCredits}</p>
                  </div>
                  <div className="bg-white rounded-xl p-5 border border-slate-200">
                    <p className="text-sm text-slate-500 mb-1">Total Spent</p>
                    <p className="text-lg font-bold text-slate-900">£{purchases.reduce((acc, p) => acc + (p.purchase?.amount || 0), 0).toFixed(2)}</p>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h3 className="font-bold text-slate-900 mb-4">Need More Credits?</h3>
                  <p className="text-slate-600 mb-4">Purchase additional credits to work on more business plans.</p>
                  <Button 
                    className="btn-primary"
                    onClick={() => {
                      if (!hasAnyCorePackage) {
                        setShowPackageGate(true);
                      } else {
                        setActiveMenu('credits');
                      }
                    }}
                  >
                    <CreditCard className="w-4 h-4 mr-2" />Buy More Credits
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* References Section */}
          {activeMenu === 'references' && (
            <div className="flex-1 p-6 overflow-y-auto">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-2xl font-bold text-slate-900 mb-6">Useful References</h2>
                <p className="text-slate-600 mb-8">Essential resources for your UK Innovator Founder Visa application</p>
                
                {/* Endorsing Bodies */}
                <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-red-500" />
                    Endorsing Bodies
                  </h3>
                  <div className="space-y-4">
                    <a href="https://envestors.co.uk/" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors group">
                      <div>
                        <p className="font-medium text-slate-900">Envestors</p>
                        <p className="text-sm text-slate-500">Leading UK endorsing body for Innovator visas</p>
                        <p className="text-xs text-red-500 mt-1">Endorsement fee: ~£1,500 - £2,000</p>
                      </div>
                      <ExternalLink className="w-5 h-5 text-slate-400 group-hover:text-red-500 transition-colors" />
                    </a>
                    <a href="https://www.techcityuk.com/visa" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors group">
                      <div>
                        <p className="font-medium text-slate-900">Tech Nation</p>
                        <p className="text-sm text-slate-500">Government-backed endorsing body for tech founders</p>
                        <p className="text-xs text-red-500 mt-1">Endorsement fee: Free (competitive selection)</p>
                      </div>
                      <ExternalLink className="w-5 h-5 text-slate-400 group-hover:text-red-500 transition-colors" />
                    </a>
                    <a href="https://startups.co.uk/visa/" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors group">
                      <div>
                        <p className="font-medium text-slate-900">Startups.co.uk</p>
                        <p className="text-sm text-slate-500">Endorsement and business support services</p>
                        <p className="text-xs text-red-500 mt-1">Endorsement fee: ~£1,800</p>
                      </div>
                      <ExternalLink className="w-5 h-5 text-slate-400 group-hover:text-red-500 transition-colors" />
                    </a>
                  </div>
                </div>
                
                {/* Official Government Links */}
                <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-blue-500" />
                    Official UK Government Resources
                  </h3>
                  <div className="space-y-4">
                    <a href="https://www.gov.uk/innovator-founder-visa" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors group">
                      <div>
                        <p className="font-medium text-slate-900">Innovator Founder Visa - GOV.UK</p>
                        <p className="text-sm text-slate-500">Official visa requirements and application process</p>
                      </div>
                      <ExternalLink className="w-5 h-5 text-slate-400 group-hover:text-blue-500 transition-colors" />
                    </a>
                    <a href="https://www.gov.uk/innovator-founder-visa/eligibility" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors group">
                      <div>
                        <p className="font-medium text-slate-900">Eligibility Requirements</p>
                        <p className="text-sm text-slate-500">Check if you qualify for the Innovator Founder visa</p>
                      </div>
                      <ExternalLink className="w-5 h-5 text-slate-400 group-hover:text-blue-500 transition-colors" />
                    </a>
                    <a href="https://www.gov.uk/government/publications/innovator-endorsing-bodies" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors group">
                      <div>
                        <p className="font-medium text-slate-900">List of Approved Endorsing Bodies</p>
                        <p className="text-sm text-slate-500">Official list of organisations that can endorse applications</p>
                      </div>
                      <ExternalLink className="w-5 h-5 text-slate-400 group-hover:text-blue-500 transition-colors" />
                    </a>
                  </div>
                </div>
                
                {/* Market Research Resources */}
                <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-500" />
                    Market Research & Statistics
                  </h3>
                  <div className="space-y-4">
                    <a href="https://www.statista.com/" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors group">
                      <div>
                        <p className="font-medium text-slate-900">Statista</p>
                        <p className="text-sm text-slate-500">Market data, statistics and consumer insights</p>
                      </div>
                      <ExternalLink className="w-5 h-5 text-slate-400 group-hover:text-green-500 transition-colors" />
                    </a>
                    <a href="https://www.ibisworld.com/united-kingdom/" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors group">
                      <div>
                        <p className="font-medium text-slate-900">IBISWorld UK</p>
                        <p className="text-sm text-slate-500">Industry market research and analysis</p>
                      </div>
                      <ExternalLink className="w-5 h-5 text-slate-400 group-hover:text-green-500 transition-colors" />
                    </a>
                    <a href="https://www.ons.gov.uk/" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors group">
                      <div>
                        <p className="font-medium text-slate-900">Office for National Statistics</p>
                        <p className="text-sm text-slate-500">UK government statistics and data</p>
                      </div>
                      <ExternalLink className="w-5 h-5 text-slate-400 group-hover:text-green-500 transition-colors" />
                    </a>
                    <a href="https://www.grandviewresearch.com/" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors group">
                      <div>
                        <p className="font-medium text-slate-900">Grand View Research</p>
                        <p className="text-sm text-slate-500">Industry analysis and market forecasts</p>
                      </div>
                      <ExternalLink className="w-5 h-5 text-slate-400 group-hover:text-green-500 transition-colors" />
                    </a>
                  </div>
                </div>
                
                {/* Business Support */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-purple-500" />
                    UK Business Resources
                  </h3>
                  <div className="space-y-4">
                    <a href="https://www.companieshouse.gov.uk/" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors group">
                      <div>
                        <p className="font-medium text-slate-900">Companies House</p>
                        <p className="text-sm text-slate-500">Register your UK company and file accounts</p>
                      </div>
                      <ExternalLink className="w-5 h-5 text-slate-400 group-hover:text-purple-500 transition-colors" />
                    </a>
                    <a href="https://www.gov.uk/set-up-business" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors group">
                      <div>
                        <p className="font-medium text-slate-900">Set Up a Business - GOV.UK</p>
                        <p className="text-sm text-slate-500">Guide to starting a business in the UK</p>
                      </div>
                      <ExternalLink className="w-5 h-5 text-slate-400 group-hover:text-purple-500 transition-colors" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Question Bank Section - Full Width */}
          {activeMenu === 'questionbank' && (
            <div className="flex-1 p-6 overflow-y-auto" data-testid="question-bank-section">
              <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-emerald-500/25">
                    <MessageSquare className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">Question Bank</h2>
                  <p className="text-slate-600">Your personal Q&amp;A history with AI and Ahmad&apos;s responses</p>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div data-testid="qb-stat-total" className="bg-white rounded-xl border border-slate-200 p-4 text-center">
                    <p className="text-3xl font-bold text-slate-900">{questionBankStats.total_questions || 0}</p>
                    <p className="text-sm text-slate-500">Total Questions</p>
                  </div>
                  <div data-testid="qb-stat-ai" className="bg-white rounded-xl border border-slate-200 p-4 text-center">
                    <p className="text-3xl font-bold text-emerald-600">{questionBankStats.ai_answered || 0}</p>
                    <p className="text-sm text-slate-500">AI Answered</p>
                  </div>
                  <div data-testid="qb-stat-ahmad" className="bg-white rounded-xl border border-slate-200 p-4 text-center">
                    <p className="text-3xl font-bold text-blue-600">{questionBankStats.admin_answered || 0}</p>
                    <p className="text-sm text-slate-500">Ahmad Answered</p>
                  </div>
                  <div data-testid="qb-stat-pending" className="bg-white rounded-xl border border-slate-200 p-4 text-center">
                    <p className="text-3xl font-bold text-amber-600">{questionBankStats.pending || 0}</p>
                    <p className="text-sm text-slate-500">Pending</p>
                  </div>
                </div>

                {/* Search Bar */}
                <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      value={questionBankSearch}
                      onChange={(e) => {
                        setQuestionBankSearch(e.target.value);
                        if (e.target.value.length >= 2 || e.target.value.length === 0) {
                          searchQuestionBank(e.target.value);
                        }
                      }}
                      placeholder="Search your questions..."
                      className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      data-testid="question-bank-search"
                    />
                  </div>
                </div>

                {/* Questions List */}
                <div className="space-y-4">
                  {questionBankLoading ? (
                    <div className="text-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-emerald-500 mx-auto mb-3" />
                      <p className="text-slate-500">Loading your questions...</p>
                    </div>
                  ) : questionBankElements.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-2xl border border-slate-200">
                      <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">No Questions Yet</h3>
                      <p className="text-slate-500 mb-6">Post comments on video chapters to build your Q&amp;A history</p>
                      <Button onClick={() => handleMenuClick('video')} className="bg-emerald-500 hover:bg-emerald-600">
                        <Video className="w-4 h-4 mr-2" />
                        Go to Video Course
                      </Button>
                    </div>
                  ) : questionBankElements}
                </div>
              </div>
            </div>
          )}

          {/* Support Ticketing Section */}
          {activeMenu === 'support' && (
            <div className="flex-1 p-6 overflow-y-auto">
              <div className="max-w-3xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-purple-500/25">
                    <HelpCircle className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">Support Center</h2>
                  <p className="text-slate-600">Get help with your Innovator Visa application</p>
                </div>
                
                {/* New Ticket Button / Form */}
                {!showNewTicketForm ? (
                  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-slate-900">Need Help?</h3>
                        <p className="text-sm text-slate-500">Create a support ticket and we&apos;ll respond within 24 hours</p>
                      </div>
                      <Button 
                        onClick={() => { setShowNewTicketForm(true); setSelectedTicket(null); }}
                        className="bg-purple-500 hover:bg-purple-600"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        New Ticket
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-6">
                    <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-6 py-4">
                      <h3 className="font-semibold text-white flex items-center gap-2">
                        <Plus className="w-5 h-5" />
                        Create New Ticket
                      </h3>
                    </div>
                    <div className="p-6 space-y-5">
                      <div className="grid md:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">Category</label>
                          <select 
                            value={newTicketCategory}
                            onChange={(e) => setNewTicketCategory(e.target.value)}
                            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          >
                            <option value="general">General Inquiry</option>
                            <option value="technical">Technical Issue</option>
                            <option value="billing">Billing Question</option>
                            <option value="feature_request">Feature Request</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">Subject</label>
                          <input 
                            type="text"
                            value={newTicketSubject}
                            onChange={(e) => setNewTicketSubject(e.target.value)}
                            placeholder="Brief summary of your issue"
                            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Message</label>
                        <textarea 
                          value={newTicketMessage}
                          onChange={(e) => setNewTicketMessage(e.target.value)}
                          placeholder="Describe your issue in detail..."
                          rows={5}
                          className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                      </div>
                      <div className="flex gap-3 justify-end">
                        <Button variant="outline" onClick={() => setShowNewTicketForm(false)}>
                          Cancel
                        </Button>
                        <Button 
                          onClick={createSupportTicket}
                          disabled={creatingTicket}
                          className="bg-purple-500 hover:bg-purple-600"
                        >
                          {creatingTicket ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                          Submit Ticket
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Tickets List - Full Width Cards */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                    <Ticket className="w-5 h-5 text-purple-500" />
                    Your Tickets
                  </h3>
                  
                  {supportTickets.length === 0 ? (
                    <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
                      <Ticket className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                      <p className="text-lg font-medium text-slate-900">No support tickets yet</p>
                      <p className="text-sm text-slate-500 mt-1">Create a ticket if you need help</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {supportTicketCards}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Book Zoom Call Section - Full Width with Calendar */}
          {activeMenu === 'zoom' && (
            <div className="flex-1 p-4 md:p-6 overflow-y-auto bg-gradient-to-br from-slate-50 to-blue-50/30">
              <div className="max-w-5xl mx-auto">
                {/* Header */}
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-blue-500/25">
                    <Video className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">Book a Strategy Call</h2>
                  <p className="text-slate-600 max-w-lg mx-auto">Schedule a personalized 30-minute consultation with Ahmad Raza to discuss your Innovator Visa journey</p>
                </div>
                
                {/* Main Booking Card */}
                <div className="bg-white rounded-3xl shadow-xl border border-slate-200 overflow-hidden">
                  {/* Step Progress */}
                  <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
                    <div className="flex items-center justify-center gap-8">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${selectedBookingDate ? 'bg-white text-blue-600' : 'bg-white/20 text-white'}`}>
                          1
                        </div>
                        <span className="text-white text-sm hidden sm:inline">Select Date</span>
                      </div>
                      <div className="w-8 h-0.5 bg-white/30" />
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${selectedSlot ? 'bg-white text-blue-600' : 'bg-white/20 text-white'}`}>
                          2
                        </div>
                        <span className="text-white text-sm hidden sm:inline">Pick Time</span>
                      </div>
                      <div className="w-8 h-0.5 bg-white/30" />
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${bookingTopic && bookingDescription ? 'bg-white text-blue-600' : 'bg-white/20 text-white'}`}>
                          3
                        </div>
                        <span className="text-white text-sm hidden sm:inline">Details</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-4 md:p-6">
                    <div className="grid lg:grid-cols-2 gap-6">
                      {/* Left: Calendar */}
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-blue-500" />
                          Select Your Preferred Date
                        </label>
                        <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200">
                          <CalendarComponent
                            mode="single"
                            selected={selectedBookingDate}
                            onSelect={(date) => {
                              setSelectedBookingDate(date);
                              setSelectedSlot(null);
                              if (date) {
                                const dateStr = date.toISOString().split('T')[0];
                                fetchAvailableSlots(dateStr);
                              }
                            }}
                            disabled={(date) => {
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              const minDate = new Date(today);
                              minDate.setDate(minDate.getDate() + 2);
                              const dayOfWeek = date.getDay();
                              return date < minDate || dayOfWeek === 0 || dayOfWeek === 6;
                            }}
                            className="rounded-xl border-0"
                            classNames={{
                              months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0 justify-center",
                              month: "space-y-4",
                              caption: "flex justify-center pt-1 relative items-center mb-4",
                              caption_label: "text-lg font-semibold text-slate-900",
                              nav: "space-x-1 flex items-center",
                              nav_button: "h-9 w-9 bg-white hover:bg-blue-50 rounded-lg border border-slate-200 p-0 opacity-100 hover:opacity-100 flex items-center justify-center",
                              nav_button_previous: "absolute left-1",
                              nav_button_next: "absolute right-1",
                              table: "w-full border-collapse",
                              head_row: "flex justify-around",
                              head_cell: "text-slate-500 rounded-md w-10 font-medium text-sm",
                              row: "flex w-full mt-2 justify-around",
                              cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-blue-100 [&:has([aria-selected])]:rounded-lg",
                              day: "h-10 w-10 p-0 font-normal aria-selected:opacity-100 hover:bg-blue-50 rounded-lg transition-colors",
                              day_selected: "bg-blue-600 text-white hover:bg-blue-700 hover:text-white focus:bg-blue-600 focus:text-white rounded-lg",
                              day_today: "bg-amber-100 text-amber-900 font-semibold",
                              day_outside: "text-slate-300 opacity-50",
                              day_disabled: "text-slate-300 opacity-50 cursor-not-allowed hover:bg-transparent",
                              day_hidden: "invisible",
                            }}
                          />
                          <p className="text-xs text-slate-500 text-center mt-3">
                            Bookings available Monday-Friday, at least 2 days in advance
                          </p>
                        </div>
                      </div>
                      
                      {/* Right: Time Slots & Details */}
                      <div className="space-y-5">
                        {/* Time Slots */}
                        <div>
                          <label className="block text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                            <Clock className="w-4 h-4 text-blue-500" />
                            Available Time Slots (UK Time)
                          </label>
                          {!selectedBookingDate ? (
                            <div className="bg-slate-50 rounded-xl p-6 border border-dashed border-slate-300 text-center">
                              <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                              <p className="text-slate-500 text-sm">Select a date to see available times</p>
                            </div>
                          ) : loadingSlots ? (
                            <div className="bg-slate-50 rounded-xl p-6 flex items-center justify-center">
                              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                            </div>
                          ) : availableSlots.length === 0 ? (
                            <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                              <p className="text-amber-800 text-sm text-center">
                                No available slots for this date. Please select another date.
                              </p>
                            </div>
                          ) : (
                            <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto p-1">
                              {availableSlots.map((slot) => (
                                <button
                                  key={slot.time}
                                  onClick={() => setSelectedSlot(slot.time)}
                                  className={`px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                                    selectedSlot === slot.time
                                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30 scale-105'
                                      : 'bg-white border border-slate-200 text-slate-700 hover:border-blue-300 hover:bg-blue-50'
                                  }`}
                                >
                                  {slot.time}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        {/* Topic & Description */}
                        <div>
                          <label className="block text-sm font-semibold text-slate-700 mb-2">Discussion Topic</label>
                          <input 
                            type="text"
                            value={bookingTopic}
                            onChange={(e) => setBookingTopic(e.target.value)}
                            placeholder="e.g., Technical Plan Review, Business Idea Validation"
                            className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-semibold text-slate-700 mb-2">Additional Details</label>
                          <textarea 
                            value={bookingDescription}
                            onChange={(e) => setBookingDescription(e.target.value)}
                            placeholder="Briefly describe what you'd like to cover in our call..."
                            rows={3}
                            className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white resize-none"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Summary & CTA */}
                    <div className="mt-6 pt-6 border-t border-slate-200">
                      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center">
                            <Video className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900">30-minute Zoom Consultation</p>
                            <p className="text-sm text-slate-500">Link sent via email within 8 hours of booking</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-3xl font-bold text-blue-600">£25</span>
                          <Button 
                            onClick={createZoomBooking}
                            disabled={creatingBooking || !selectedBookingDate || !selectedSlot || !bookingTopic.trim() || !bookingDescription.trim()}
                            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 px-8 py-3 text-base shadow-lg shadow-blue-500/25"
                          >
                            {creatingBooking ? (
                              <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Processing...</>
                            ) : (
                              <><CreditCard className="w-5 h-5 mr-2" />Pay & Book</>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Your Bookings */}
                {zoomBookings.length > 0 && (
                  <div className="mt-6 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="bg-slate-50 px-6 py-4 border-b border-slate-200">
                      <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                        Your Upcoming Calls
                      </h3>
                    </div>
                    <div className="p-4 space-y-3">
                      {bookingElements}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Buy Credits Section - Full Width */}
          {activeMenu === 'credits' && (
            <>
              {!hasAnyCorePackage ? (
                <div className="flex-1 flex flex-col items-center justify-center p-8">
                  <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                    <Lock className="w-10 h-10 text-slate-400" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Credits Locked</h3>
                  <p className="text-slate-600 text-center mb-6 max-w-md">
                    Purchase a package first to unlock the ability to buy additional credits.
                  </p>
                  <Button onClick={() => setShowPackageGate(true)} className="bg-red-500 hover:bg-red-600">
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    View Packages
                  </Button>
                </div>
              ) : (
            <div className="flex-1 p-6 overflow-y-auto">
              <div className="max-w-3xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-amber-500/25">
                    <Zap className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">Buy Credits</h2>
                  <p className="text-slate-600">Power your AI-assisted visa application journey</p>
                </div>
                
                {/* Current Balance - Horizontal */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <h3 className="font-semibold text-slate-900">Your Current Balance</h3>
                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center">
                          <Brain className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">AI Credits</p>
                          <p className="text-xl font-bold text-amber-600">{creditsRemaining}</p>
                        </div>
                      </div>
                      <div className="w-px h-10 bg-slate-200" />
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center">
                          <Mic className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Interview Credits</p>
                          <p className="text-xl font-bold text-red-600">{interviewFreeAvailable ? '1 FREE' : interviewCredits}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Credit Packages - Stacked Full Width */}
                <div className="space-y-4">
                  {/* AI Credits Package */}
                  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 hover:border-amber-300 transition-colors">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-xl bg-amber-100 flex items-center justify-center">
                          <Brain className="w-7 h-7 text-amber-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">AI Credits Pack</h3>
                          <p className="text-sm text-slate-500">20 discussion credits for idea refinement</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-2xl font-bold text-slate-900">£9.99</span>
                        <Button 
                          onClick={() => {
                            setSelectedPaymentPackage({ id: 'ai_credits_20', name: 'AI Credits Pack (20)', price: 9.99 });
                            setShowPaymentModal(true);
                          }}
                          className="bg-amber-500 hover:bg-amber-600 px-6"
                        >
                          Buy Now
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Interview Credit */}
                  <div className="bg-white rounded-2xl border-2 border-red-200 shadow-sm p-6 relative">
                    <div className="absolute -top-3 right-6 px-3 py-1 bg-red-500 text-white text-xs font-medium rounded-full">POPULAR</div>
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-xl bg-red-100 flex items-center justify-center">
                          <Mic className="w-7 h-7 text-red-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">Mock Interview Credit</h3>
                          <p className="text-sm text-slate-500">Full 10-question interview with AI scoring</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-2xl font-bold text-slate-900">£9.99</span>
                        <Button 
                          onClick={() => {
                            setSelectedPaymentPackage({ id: 'interview_1', name: 'Mock Interview Credit', price: 9.99 });
                            setShowPaymentModal(true);
                          }}
                          className="bg-red-500 hover:bg-red-600 px-6"
                        >
                          Buy Now
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Zoom Call */}
                  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 hover:border-blue-300 transition-colors">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center">
                          <Phone className="w-7 h-7 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">Zoom Consultation</h3>
                          <p className="text-sm text-slate-500">30-minute 1-on-1 call with Ahmad Raza</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-2xl font-bold text-slate-900">£25</span>
                        <Button onClick={() => setActiveMenu('zoom')} className="bg-blue-500 hover:bg-blue-600 px-6">Book Call</Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* AI Idea Engine Modal */}
      <AnimatePresence>
        {showIdeaEngine && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            className="fixed inset-0 bg-slate-900/90 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            data-testid="idea-engine-modal"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }} 
              animate={{ scale: 1, opacity: 1, y: 0 }} 
              exit={{ scale: 0.9, opacity: 0, y: 20 }} 
              transition={{ type: "spring", duration: 0.5 }}
              className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-3xl max-w-3xl w-full overflow-hidden shadow-2xl border border-slate-700/50"
              onClick={e => e.stopPropagation()}
            >
              {/* Header with Ahmad Raza Branding */}
              <div className="relative px-6 py-5 border-b border-slate-700/50 bg-gradient-to-r from-red-500/10 via-transparent to-transparent">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-lg shadow-red-500/25">
                    <Brain className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                      AI Idea Discovery Engine
                      <span className="text-xs px-2 py-0.5 bg-red-500/20 text-red-400 rounded-full font-medium">v2</span>
                    </h2>
                    <p className="text-slate-400 text-sm">Trained by <span className="text-red-400 font-medium">Ahmad Raza</span> • UK Innovator Visa Expert</p>
                  </div>
                </div>
                <button 
                  onClick={handleCancelIdeaEngine} 
                  className="absolute top-4 right-4 w-10 h-10 rounded-full bg-slate-800/80 hover:bg-slate-700 flex items-center justify-center transition-colors"
                >
                  <X className="w-5 h-5 text-slate-400" />
                </button>
                
                {/* Enhanced Stage Indicator */}
                <div className="mt-4 flex items-center gap-3">
                  <div className="flex items-center gap-1.5">
                    {(() => {
                      const stages = ['welcome', 'discovery', 'generating', 'ready_to_lock'];
                      const stageElements = [];
                      for (let idx = 0; idx < stages.length; idx++) {
                        const stage = stages[idx];
                        const currentIdx = stages.indexOf(ideaEngineStage);
                        const dotClass = ideaEngineStage === stage ? 'bg-red-500 scale-125' : 
                          currentIdx > idx ? 'bg-red-400' : 'bg-slate-600';
                        stageElements.push(
                          <div key={stage} className={`w-2 h-2 rounded-full transition-all ${dotClass}`} />
                        );
                      }
                      return stageElements;
                    })()}
                  </div>
                  <span className="text-xs text-slate-500 font-medium">
                    {ideaEngineStage === 'welcome' && 'Getting Started'}
                    {ideaEngineStage === 'discovery' && 'Discovery in Progress'}
                    {(ideaEngineStage === 'generating' || ideaEngineStage === 'ideas_presented') && 'Ideas Generated'}
                    {ideaEngineStage === 'ready_to_lock' && 'Ready to Lock!'}
                    {!['welcome', 'discovery', 'generating', 'ideas_presented', 'ready_to_lock'].includes(ideaEngineStage) && ideaEngineStage}
                  </span>
                  <div className="flex-1" />
                  <span className="text-xs text-slate-600 flex items-center gap-1">
                    <CreditCard className="w-3 h-3" />
                    {creditsRemaining} credits
                  </span>
                </div>
              </div>

              {/* Chat Area */}
              <div 
                ref={ideaEngineScrollRef}
                className="h-[400px] overflow-y-auto p-6 space-y-4 bg-gradient-to-b from-transparent via-slate-800/20 to-slate-800/40"
              >
                {ideaEngineStarting ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <div className="w-16 h-16 mx-auto mb-4 relative">
                        <div className="absolute inset-0 rounded-full border-4 border-red-500/30 animate-ping" />
                        <div className="relative w-full h-full rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center">
                          <Sparkles className="w-8 h-8 text-white animate-pulse" />
                        </div>
                      </div>
                      <p className="text-slate-400 animate-pulse">Initializing AI Idea Engine...</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {(() => {
                      const msgElements = [];
                      for (let i = 0; i < ideaEngineMessages.length; i++) {
                        const msg = ideaEngineMessages[i];
                        const isUser = msg.role === 'user';
                        const displayContent = isUser ? msg.content : cleanAIMessage(msg.content);
                        msgElements.push(
                          <motion.div 
                            key={i} 
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`flex items-start gap-3 ${isUser ? 'flex-row-reverse' : ''}`}
                          >
                            <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${isUser ? 'bg-slate-700' : 'bg-gradient-to-br from-red-500 to-red-600'}`}>
                              {isUser ? <User className="w-4 h-4 text-slate-300" /> : <Brain className="w-4 h-4 text-white" />}
                            </div>
                            <div className={`max-w-[75%] rounded-2xl px-4 py-3 ${isUser ? 'bg-slate-700 text-white rounded-tr-sm' : 'bg-slate-800/80 border border-slate-700/50 text-slate-200 rounded-tl-sm'}`}>
                              <p className="whitespace-pre-wrap text-sm leading-relaxed">{displayContent}</p>
                            </div>
                          </motion.div>
                        );
                      }
                      return msgElements;
                    })()}
                    
                    {ideaEngineSending && (
                      <motion.div 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-start gap-3"
                      >
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center flex-shrink-0">
                          <Brain className="w-4 h-4 text-white" />
                        </div>
                        <div className="bg-slate-800/80 border border-slate-700/50 rounded-2xl rounded-tl-sm px-4 py-3 min-w-[200px]">
                          {animatedState ? (
                            <div className="flex items-center gap-3">
                              {animatedState === 'THINKING' && <Loader2 className="w-4 h-4 text-red-400 animate-spin" />}
                              {animatedState === 'RESEARCHING_MARKETS' && <Search className="w-4 h-4 text-blue-400 animate-pulse" />}
                              {animatedState === 'EXPLORING_INNOVATION' && <Lightbulb className="w-4 h-4 text-yellow-400 animate-pulse" />}
                              {animatedState === 'ASSESSING_SCALABILITY' && <TrendingUp className="w-4 h-4 text-green-400 animate-pulse" />}
                              {animatedState === 'GENERATING_CONCEPTS' && <Sparkles className="w-4 h-4 text-purple-400 animate-pulse" />}
                              {animatedState === 'EVALUATING_ENDORSEMENT' && <Shield className="w-4 h-4 text-red-400 animate-pulse" />}
                              <span className="text-sm text-slate-300">
                                {animatedState === 'THINKING' && 'Thinking...'}
                                {animatedState === 'RESEARCHING_MARKETS' && 'Researching Market Opportunities...'}
                                {animatedState === 'EXPLORING_INNOVATION' && 'Exploring Innovation Possibilities...'}
                                {animatedState === 'ASSESSING_SCALABILITY' && 'Assessing Scalability...'}
                                {animatedState === 'GENERATING_CONCEPTS' && 'Generating Business Concepts...'}
                                {animatedState === 'EVALUATING_ENDORSEMENT' && 'Evaluating Endorsement Potential...'}
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 bg-red-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                              <div className="w-2 h-2 bg-red-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                              <div className="w-2 h-2 bg-red-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </>
                )}
              </div>

              {/* Finalization Panel (when ready) */}
              {ideaEngineReady && (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="px-6 py-4 bg-gradient-to-r from-green-500/10 via-emerald-500/10 to-green-500/5 border-t border-green-500/20"
                >
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">Your idea is ready to be locked!</h3>
                      <p className="text-slate-400 text-sm">Confirm your business name and category to proceed</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1.5">Business Name</label>
                      <input 
                        type="text" 
                        value={ideaEngineProposedName} 
                        onChange={(e) => setIdeaEngineProposedName(e.target.value)}
                        placeholder="Your business name"
                        className="w-full px-3 py-2.5 bg-slate-800 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500"
                        data-testid="idea-engine-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1.5">Category</label>
                      <select 
                        value={ideaEngineCategory} 
                        onChange={(e) => setIdeaEngineCategory(e.target.value)}
                        className="w-full px-3 py-2.5 bg-slate-800 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500"
                        data-testid="idea-engine-category-select"
                      >
                        <option value="Tech">Tech</option>
                        <option value="Healthcare">Healthcare</option>
                        <option value="Finance">Finance</option>
                        <option value="Education">Education</option>
                        <option value="Retail">Retail</option>
                        <option value="Services">Services</option>
                        <option value="Manufacturing">Manufacturing</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <Button 
                      variant="outline" 
                      className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
                      onClick={() => setIdeaEngineReady(false)}
                    >
                      Continue Discussing
                    </Button>
                    <Button 
                      className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white"
                      onClick={handleFinalizeIdea}
                      disabled={!ideaEngineProposedName.trim()}
                      data-testid="idea-engine-finalize-btn"
                    >
                      <Rocket className="w-4 h-4 mr-2" />
                      Lock Idea & Create Proposal
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* Input Area */}
              {!ideaEngineReady && !ideaEngineStarting && (
                <div className="px-6 py-4 border-t border-slate-700/50 bg-slate-800/50">
                  <div className="flex gap-3">
                    <input 
                      type="text" 
                      value={ideaEngineInput}
                      onChange={(e) => setIdeaEngineInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleIdeaEngineChat()}
                      placeholder="Share your thoughts..."
                      className="flex-1 px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-red-500/50 focus:border-red-500"
                      disabled={ideaEngineSending}
                      data-testid="idea-engine-input"
                    />
                    <Button 
                      onClick={handleIdeaEngineChat}
                      disabled={!ideaEngineInput.trim() || ideaEngineSending}
                      className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 px-6"
                      data-testid="idea-engine-send-btn"
                    >
                      {ideaEngineSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-2 text-center">
                    The AI will ask 4-5 questions to understand and validate your business idea
                  </p>
                </div>
              )}

              {/* Credit Warning Footer */}
              <div className="px-6 py-3 bg-slate-900/80 border-t border-slate-700/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <AlertCircle className="w-3.5 h-3.5" />
                    <span>Each message uses 1 credit • {creditsRemaining} credits remaining</span>
                  </div>
                  <button 
                    onClick={handleCancelIdeaEngine}
                    className="text-xs text-slate-500 hover:text-slate-400 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Rename Modal */}
      <AnimatePresence>
        {showRenameModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowRenameModal(false)}>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
              <h2 className="text-xl font-bold text-slate-900 mb-4">Rename Proposal</h2>
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">Proposal Name</label>
                <input type="text" value={renameValue} onChange={(e) => setRenameValue(e.target.value)} placeholder="Enter new name" className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500" autoFocus />
              </div>
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setShowRenameModal(false)}>Cancel</Button>
                <Button className="btn-primary flex-1" onClick={handleRenameProposal} disabled={!renameValue.trim()}>Save</Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Video Guide Modal */}
      <AnimatePresence>
        {showVideoModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowVideoModal(false)}>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="bg-slate-900 rounded-2xl max-w-3xl w-full overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
              <div className="relative aspect-video bg-black">
                <img src="https://customer-assets.emergentagent.com/job_fee64ab8-866e-4873-bfd8-a2b3caadae98/artifacts/vumr99ag_ChatGPT%20Image%20Jan%2031%2C%202026%2C%2012_12_42%20PM.png" alt="Video guide" className="w-full h-full object-cover object-top" />
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                  <button className="w-20 h-20 rounded-full bg-red-500 flex items-center justify-center hover:bg-red-600 transition-colors shadow-xl">
                    <Play className="w-8 h-8 text-white ml-1" />
                  </button>
                </div>
                <button onClick={() => setShowVideoModal(false)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center transition-colors">
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
              <div className="p-6">
                <h3 className="text-white text-xl font-bold mb-2">{currentSection?.title} - Video Guide</h3>
                <p className="text-slate-400">In this video, I&apos;ll explain what you need to cover in this section and share tips for creating a compelling proposal.</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Locked Idea Warning Modal */}
      <AnimatePresence>
        {showLockedIdeaModal && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            className="fixed inset-0 bg-slate-900/90 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            data-testid="locked-idea-modal"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }} 
              animate={{ scale: 1, opacity: 1, y: 0 }} 
              exit={{ scale: 0.9, opacity: 0, y: 20 }} 
              className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-5">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                    <Lock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Idea Already Locked</h2>
                    <p className="text-white/80 text-sm">You have an active business idea</p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Locked Idea Display */}
                <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 mb-6 border border-slate-200">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide font-medium mb-1">Your Locked Idea</p>
                      <h3 className="font-bold text-slate-900 text-lg">{lockedIdea?.title || lockedIdea?.idea_title || 'Your Business Idea'}</h3>
                      {lockedIdea?.category && (
                        <Badge className="mt-2 bg-slate-200 text-slate-700">{lockedIdea.category}</Badge>
                      )}
                    </div>
                  </div>
                </div>

                {/* Explanation */}
                <div className="space-y-4 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <AlertCircle className="w-4 h-4 text-amber-600" />
                    </div>
                    <p className="text-sm text-slate-700">
                      Your remaining <strong>{creditsRemaining} AI credits</strong> can only be used to build your proposal for this locked idea, not to explore new business ideas.
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Lightbulb className="w-4 h-4 text-blue-600" />
                    </div>
                    <p className="text-sm text-slate-700">
                      To explore <strong>new business ideas</strong>, please purchase a new Idea Credit Pack.
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-3">
                  <Button 
                    className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                    onClick={() => {
                      setShowLockedIdeaModal(false);
                      // Select the locked proposal and go to build it
                      const lockedProposal = proposals.find(p => p.id === lockedIdea?.id);
                      if (lockedProposal) {
                        setSelectedProposal(lockedProposal);
                        setSelectedSection('executive_summary');
                      }
                      setActiveMenu('proposals');
                    }}
                    data-testid="continue-building-btn"
                  >
                    <Rocket className="w-4 h-4 mr-2" />
                    Continue Building My Proposal
                  </Button>
                  <Button 
                    variant="outline"
                    className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
                    onClick={() => {
                      setShowLockedIdeaModal(false);
                      setActiveMenu('credits');
                    }}
                    data-testid="buy-more-credits-btn"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Buy New Idea Credit Pack
                  </Button>
                  <button 
                    className="text-sm text-slate-500 hover:text-slate-700 transition-colors"
                    onClick={() => setShowLockedIdeaModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Image Gallery Modal */}
      <AnimatePresence>
        {showImageGallery && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            className="fixed inset-0 bg-slate-900/90 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowImageGallery(false)}
            data-testid="image-gallery-modal"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }} 
              animate={{ scale: 1, opacity: 1, y: 0 }} 
              exit={{ scale: 0.9, opacity: 0, y: 20 }} 
              className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                    <ImagePlus className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">Add Section Image</h2>
                    <p className="text-white/80 text-sm">Choose an image to enhance your proposal</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowImageGallery(false)} 
                  className="p-2 rounded-lg hover:bg-white/20 transition-colors"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>

              {/* Search Bar */}
              <div className="p-4 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      value={imageSearchQuery}
                      onChange={(e) => setImageSearchQuery(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && searchImages(imageSearchQuery)}
                      placeholder="Search for images..."
                      className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                  <Button 
                    onClick={() => searchImages(imageSearchQuery)}
                    disabled={searchingImages}
                    className="bg-red-500 hover:bg-red-600"
                  >
                    {searchingImages ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              {/* Image Grid */}
              <div className="flex-1 overflow-y-auto p-4">
                {searchingImages ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
                  </div>
                ) : imageSearchResults.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {(() => {
                      const imageElements = [];
                      for (let i = 0; i < imageSearchResults.length; i++) {
                        const img = imageSearchResults[i];
                        imageElements.push(
                          <button
                            key={img.id}
                            onClick={() => selectSectionImage(img.url)}
                            className="group relative aspect-video rounded-xl overflow-hidden border-2 border-transparent hover:border-red-500 transition-all"
                          >
                            <img
                              src={img.thumb || img.url}
                              alt={img.alt}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                              <Check className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                            {img.credit && (
                              <p className="absolute bottom-1 left-1 right-1 text-[10px] text-white/70 bg-black/50 px-1 rounded truncate opacity-0 group-hover:opacity-100 transition-opacity">
                                {img.credit}
                              </p>
                            )}
                          </button>
                        );
                      }
                      return imageElements;
                    })()}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                    <ImagePlus className="w-12 h-12 mb-3 text-slate-300" />
                    <p>No images found. Try a different search term.</p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
                <p className="text-xs text-slate-500">Sample images for visualization. Replace with your own or search for more.</p>
                <Button variant="outline" onClick={() => setShowImageGallery(false)}>
                  Cancel
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Section Review & Approve Modal */}
      <AnimatePresence>
        {showReviewModal && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            className="fixed inset-0 bg-slate-900/90 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowReviewModal(false)}
            data-testid="review-modal"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }} 
              animate={{ scale: 1, opacity: 1, y: 0 }} 
              exit={{ scale: 0.9, opacity: 0, y: 20 }} 
              className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                    <Edit3 className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">Review & Approve</h2>
                    <p className="text-white/80 text-sm">{currentSection?.title}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowReviewModal(false)}
                  className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>

              {/* Content Editor */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Edit the generated content before approving:
                  </label>
                  <textarea
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    className="w-full h-96 px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-500 resize-none text-slate-800"
                    placeholder="Section content..."
                  />
                </div>

                {/* Citations Display */}
                {sectionCitations.length > 0 && (
                  <div className="bg-slate-50 rounded-xl p-4">
                    <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                      <BookOpen className="w-4 h-4" />
                      Citations Included
                    </h4>
                    <div className="space-y-1">
                      {(() => {
                        const citeEls = [];
                        for (let i = 0; i < sectionCitations.length; i++) {
                          citeEls.push(
                            <p key={i} className="text-sm text-slate-600">
                              <span className="font-medium text-red-600">[{i + 1}]</span> {sectionCitations[i]}
                            </p>
                          );
                        }
                        return citeEls;
                      })()}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer Actions */}
              <div className="border-t border-slate-200 px-6 py-4 bg-slate-50 flex items-center justify-between">
                <Button
                  variant="outline"
                  onClick={() => setShowReviewModal(false)}
                  className="text-slate-600"
                >
                  Cancel
                </Button>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    onClick={handleRegenerateSection}
                    disabled={sectionSaving}
                    className="text-slate-600"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Regenerate
                  </Button>
                  <Button
                    onClick={handleApproveSection}
                    disabled={sectionSaving || !editedContent.trim()}
                    className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
                  >
                    {sectionSaving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Approve & Continue
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Interview Credit Purchase Modal */}
      <AnimatePresence>
        {showInterviewPurchaseModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={() => setShowInterviewPurchaseModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                    <Mic className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">Interview Credits</h2>
                    <p className="text-white/80 text-sm">Practice for your endorsement interview</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowInterviewPurchaseModal(false)}
                  className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Current Balance */}
                <div className="bg-slate-50 rounded-xl p-4 mb-6 border border-slate-200">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Your Current Balance</span>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                        <Mic className="w-4 h-4 text-red-600" />
                      </div>
                      <span className="text-2xl font-bold text-slate-900">
                        {interviewFreeAvailable ? '1 FREE' : interviewCredits}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Purchase Option */}
                <div className="border-2 border-red-200 rounded-xl p-5 bg-red-50/50 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-bold text-slate-900">Mock Interview Credit</h3>
                      <p className="text-sm text-slate-600">1 full interview session</p>
                    </div>
                    <div className="text-right">
                      <span className="text-3xl font-bold text-red-600">£9.99</span>
                    </div>
                  </div>
                  
                  <div className="space-y-2 text-sm text-slate-600 mb-4">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      <span>10 professional interview questions</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      <span>AI interviewer with voice narration</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      <span>Detailed scoring & feedback</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      <span>Per-question improvement tips</span>
                    </div>
                  </div>

                  <Button
                    onClick={purchaseInterviewCredit}
                    disabled={purchasingInterviewCredit}
                    className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 py-4 text-lg"
                    data-testid="purchase-interview-credit-btn"
                  >
                    {purchasingInterviewCredit ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-5 h-5 mr-2" />
                        Purchase Now - £9.99
                      </>
                    )}
                  </Button>
                </div>

                {/* Info Note */}
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                  <div className="flex items-start gap-2 text-sm text-blue-700">
                    <Lightbulb className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>Each credit allows one complete mock interview session. Credits do not expire.</span>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="border-t border-slate-200 px-6 py-4 bg-slate-50 flex justify-end">
                <Button
                  variant="outline"
                  onClick={() => setShowInterviewPurchaseModal(false)}
                  className="text-slate-600"
                >
                  Maybe Later
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Document Preview Modal */}
      <DocumentPreviewModal
        isOpen={showDocumentPreview}
        onClose={() => setShowDocumentPreview(false)}
        proposal={selectedProposal}
        sections={TECHNICAL_SECTIONS}
        completedSections={completedSections}
        selectedTemplate={selectedTemplate}
        token={token}
        API_URL={API}
      />

      {/* Profile Settings Modal */}
      <AnimatePresence>
        {showProfileModal && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowProfileModal(false)}
          >
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }} 
              animate={{ scale: 1, opacity: 1 }} 
              exit={{ scale: 0.95, opacity: 0 }} 
              className="bg-white rounded-2xl max-w-md w-full shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-slate-900 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                      <UserCircle className="w-5 h-5 text-red-600" />
                    </div>
                    Profile Settings
                  </h2>
                  <button onClick={() => setShowProfileModal(false)} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center">
                    <X className="w-5 h-5 text-slate-400" />
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Full Name</label>
                  <input 
                    type="text"
                    value={profileName}
                    onChange={(e) => setProfileName(e.target.value)}
                    placeholder="Enter your full name"
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email Address</label>
                  <input 
                    type="email"
                    value={userEmail}
                    disabled
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl bg-slate-50 text-slate-500"
                  />
                  <p className="text-xs text-slate-400 mt-1">Email cannot be changed</p>
                </div>
                
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" onClick={() => setShowProfileModal(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button onClick={saveProfileSettings} disabled={profileSaving} className="flex-1 bg-red-500 hover:bg-red-600">
                    {profileSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Changes
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Change Password Modal */}
      <AnimatePresence>
        {showPasswordModal && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowPasswordModal(false)}
          >
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }} 
              animate={{ scale: 1, opacity: 1 }} 
              exit={{ scale: 0.95, opacity: 0 }} 
              className="bg-white rounded-2xl max-w-md w-full shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-slate-900 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                      <Key className="w-5 h-5 text-amber-600" />
                    </div>
                    Change Password
                  </h2>
                  <button onClick={() => setShowPasswordModal(false)} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center">
                    <X className="w-5 h-5 text-slate-400" />
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Current Password</label>
                  <div className="relative">
                    <input 
                      type={showCurrentPassword ? "text" : "password"}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter current password"
                      className="w-full px-4 py-3 pr-12 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    />
                    <button 
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">New Password</label>
                  <div className="relative">
                    <input 
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                      className="w-full px-4 py-3 pr-12 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    />
                    <button 
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Minimum 8 characters</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Confirm New Password</label>
                  <input 
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  />
                </div>
                
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" onClick={() => setShowPasswordModal(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button onClick={changePassword} disabled={passwordChanging} className="flex-1 bg-amber-500 hover:bg-amber-600">
                    {passwordChanging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Key className="w-4 h-4 mr-2" />}
                    Update Password
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Billing & Invoices Modal */}
      <AnimatePresence>
        {showBillingModal && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowBillingModal(false)}
          >
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }} 
              animate={{ scale: 1, opacity: 1 }} 
              exit={{ scale: 0.95, opacity: 0 }} 
              className="bg-white rounded-2xl max-w-lg w-full shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-slate-900 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                      <Receipt className="w-5 h-5 text-green-600" />
                    </div>
                    Billing & Invoices
                  </h2>
                  <button onClick={() => setShowBillingModal(false)} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center">
                    <X className="w-5 h-5 text-slate-400" />
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                {/* Current Package */}
                <div className="bg-gradient-to-r from-red-50 to-amber-50 rounded-xl p-4 border border-red-100 mb-6">
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Current Package</p>
                  <p className="text-lg font-bold text-slate-900">{packageName}</p>
                </div>
                
                {/* Purchase History */}
                <h3 className="font-semibold text-slate-900 mb-3">Purchase History</h3>
                {purchases.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Receipt className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No purchases yet</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {billingHistoryElements}
                  </div>
                )}
                
                <div className="mt-6 pt-4 border-t border-slate-200">
                  <Button variant="outline" onClick={() => setShowBillingModal(false)} className="w-full">
                    Close
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Account Settings Modal */}
      <AnimatePresence>
        {showAccountSettingsModal && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowAccountSettingsModal(false)}
          >
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }} 
              animate={{ scale: 1, opacity: 1 }} 
              exit={{ scale: 0.95, opacity: 0 }} 
              className="bg-white rounded-2xl max-w-md w-full shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-slate-900 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                      <Settings className="w-5 h-5 text-blue-600" />
                    </div>
                    Account Settings
                  </h2>
                  <button onClick={() => setShowAccountSettingsModal(false)} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center">
                    <X className="w-5 h-5 text-slate-400" />
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-4">
                {/* Email Preferences */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div>
                    <p className="font-medium text-slate-900">Email Notifications</p>
                    <p className="text-sm text-slate-500">Receive updates about your application</p>
                  </div>
                  <div className="w-12 h-6 bg-green-500 rounded-full relative cursor-pointer">
                    <div className="w-5 h-5 bg-white rounded-full absolute right-0.5 top-0.5 shadow" />
                  </div>
                </div>
                
                {/* Language */}
                <div className="p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">Language</p>
                      <p className="text-sm text-slate-500">Display language</p>
                    </div>
                    <select className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                      <option>English</option>
                    </select>
                  </div>
                </div>
                
                {/* Account Info */}
                <div className="p-4 bg-slate-50 rounded-xl">
                  <p className="font-medium text-slate-900 mb-2">Account Information</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Account ID</span>
                      <span className="text-slate-700 font-mono">{user?.id?.slice(-8) || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Member Since</span>
                      <span className="text-slate-700">{user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</span>
                    </div>
                  </div>
                </div>
                
                {/* Danger Zone */}
                <div className="pt-4 border-t border-slate-200">
                  <p className="text-sm font-medium text-red-600 mb-3">Danger Zone</p>
                  <Button variant="outline" className="w-full border-red-200 text-red-600 hover:bg-red-50">
                    Delete Account
                  </Button>
                  <p className="text-xs text-slate-400 mt-2 text-center">This action cannot be undone</p>
                </div>
                
                <div className="pt-2">
                  <Button variant="outline" onClick={() => setShowAccountSettingsModal(false)} className="w-full">
                    Close
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Payment Modal */}
      {/* Package Gate Modal - Forces user to select a package before accessing features */}
      <AnimatePresence>
        {showPackageGate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-red-500 to-red-600 px-8 py-6 rounded-t-3xl">
                <h2 className="text-2xl font-bold text-white">Choose Your Package</h2>
                <p className="text-red-100 mt-1">Select a package to unlock all features</p>
              </div>

              <div className="p-8">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Lock className="w-8 h-8 text-amber-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Welcome to Innovator Founder Visas!</h3>
                  <p className="text-slate-600">To access all features, please select one of our packages below.</p>
                </div>

                {/* Package Cards */}
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  {/* Video Course Package */}
                  <div className="border-2 border-slate-200 rounded-2xl p-6 hover:border-blue-300 hover:shadow-lg transition-all">
                    <div className="text-center">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                        <Video className="w-6 h-6 text-blue-600" />
                      </div>
                      <h4 className="font-bold text-slate-900 mb-1">Video Course</h4>
                      <p className="text-3xl font-bold text-slate-900 mb-2">
                        £{availablePackages?.video?.price || '29.99'}
                      </p>
                      <p className="text-sm text-slate-500 mb-4">One-time payment</p>
                      <ul className="text-sm text-slate-600 space-y-2 text-left mb-6">
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-500" /> Comprehensive video course
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-500" /> Step-by-step guidance
                        </li>
                        <li className="flex items-center gap-2">
                          <X className="w-4 h-4 text-slate-300" /> AI Proposal Builder
                        </li>
                        <li className="flex items-center gap-2">
                          <X className="w-4 h-4 text-slate-300" /> Mock Interview System
                        </li>
                      </ul>
                      <Button
                        onClick={() => {
                          setSelectedPaymentPackage({ id: 'video', name: 'Video Course', price: availablePackages?.video?.price || 29.99 });
                          setShowPackageGate(false);
                          setShowPaymentModal(true);
                        }}
                        className="w-full bg-blue-500 hover:bg-blue-600"
                      >
                        Select Package
                      </Button>
                    </div>
                  </div>

                  {/* Complete Package - Highlighted */}
                  <div className="border-2 border-red-400 rounded-2xl p-6 relative bg-gradient-to-b from-red-50 to-white shadow-lg">
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-red-500 text-white">Most Popular</Badge>
                    </div>
                    <div className="text-center">
                      <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                        <Rocket className="w-6 h-6 text-red-600" />
                      </div>
                      <h4 className="font-bold text-slate-900 mb-1">Complete System</h4>
                      <p className="text-3xl font-bold text-red-600 mb-2">
                        £{availablePackages?.complete?.price || '149'}
                      </p>
                      <p className="text-sm text-slate-500 mb-4">One-time payment</p>
                      <ul className="text-sm text-slate-600 space-y-2 text-left mb-6">
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-500" /> Everything in Video Course
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-500" /> AI Proposal Builder
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-500" /> Mock Interview System
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-500" /> 50 AI Credits included
                        </li>
                      </ul>
                      <Button
                        onClick={() => {
                          setSelectedPaymentPackage({ id: 'complete', name: 'Complete System', price: availablePackages?.complete?.price || 149 });
                          setShowPackageGate(false);
                          setShowPaymentModal(true);
                        }}
                        className="w-full bg-red-500 hover:bg-red-600"
                      >
                        Select Package
                      </Button>
                    </div>
                  </div>

                  {/* Premium Package */}
                  <div className="border-2 border-slate-200 rounded-2xl p-6 hover:border-purple-300 hover:shadow-lg transition-all">
                    <div className="text-center">
                      <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                        <Briefcase className="w-6 h-6 text-purple-600" />
                      </div>
                      <h4 className="font-bold text-slate-900 mb-1">Premium White-Glove</h4>
                      <p className="text-3xl font-bold text-slate-900 mb-2">
                        £{availablePackages?.premium?.price || '1,999'}
                      </p>
                      <p className="text-sm text-slate-500 mb-4">One-time payment</p>
                      <ul className="text-sm text-slate-600 space-y-2 text-left mb-6">
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-500" /> Everything in Complete
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-500" /> Done-for-you service
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-500" /> Expert consultation
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-500" /> Priority support
                        </li>
                      </ul>
                      <Button
                        onClick={() => {
                          setSelectedPaymentPackage({ id: 'premium', name: 'Premium White-Glove', price: availablePackages?.premium?.price || 1999 });
                          setShowPackageGate(false);
                          setShowPaymentModal(true);
                        }}
                        className="w-full bg-purple-500 hover:bg-purple-600"
                      >
                        Select Package
                      </Button>
                    </div>
                  </div>
                </div>

                <p className="text-center text-sm text-slate-500">
                  Need help choosing? <a href="mailto:support@innovatorfoundervisas.com" className="text-red-500 hover:underline">Contact our team</a>
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Upgrade Package Modal */}
      <AnimatePresence>
        {showUpgradeModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowUpgradeModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-8 py-6 rounded-t-3xl relative">
                <button 
                  onClick={() => setShowUpgradeModal(false)}
                  className="absolute top-4 right-4 text-white/80 hover:text-white p-1"
                >
                  <X className="w-6 h-6" />
                </button>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <Zap className="w-6 h-6 text-yellow-300" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Upgrade Your Package</h2>
                    <p className="text-purple-100">Unlock more features and accelerate your visa journey</p>
                  </div>
                </div>
              </div>

              <div className="p-8">
                {/* Current Package Display */}
                <div className="mb-8 p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <p className="text-sm text-slate-500 mb-1">Your Current Package</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-200 rounded-lg flex items-center justify-center">
                      {currentPackageType === 'video' && <Video className="w-5 h-5 text-slate-600" />}
                      {currentPackageType === 'complete' && <Rocket className="w-5 h-5 text-slate-600" />}
                      {!currentPackageType && <Lock className="w-5 h-5 text-slate-400" />}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{packageName}</p>
                      <p className="text-xs text-slate-500">
                        {currentPackageType === 'video' && 'Video Course Access'}
                        {currentPackageType === 'complete' && 'Full System Access'}
                        {!currentPackageType && 'No package yet'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Upgrade Options */}
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-500" />
                  Available Upgrades
                </h3>
                
                <div className="space-y-4">
                  {upgradeOptionElements}
                </div>

                {upgradeOptions.length === 0 && (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-8 h-8 text-green-500" />
                    </div>
                    <h4 className="font-semibold text-slate-900 mb-2">You have the best package!</h4>
                    <p className="text-slate-600">You already have access to all features. Enjoy!</p>
                  </div>
                )}

                <p className="text-center text-sm text-slate-500 mt-6">
                  Questions about upgrades? <a href="mailto:support@innovatorfoundervisas.com" className="text-purple-500 hover:underline">Contact support</a>
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        packageId={selectedPaymentPackage?.id}
        packageName={selectedPaymentPackage?.name}
        packagePrice={selectedPaymentPackage?.price}
        packageDescription=""
        onSuccess={() => {
          setShowPaymentModal(false);
          // Refresh user data after successful payment
          window.location.reload();
        }}
        token={token}
        userEmail={userEmail}
        userName={userName}
      />
    </div>
  );
}

export default UserDashboard;
